(() => {
  /* Page-weight pass (owner 10-30 "Implement"): non-urgent try-on work waits until the page has loaded and the phone is idle. */
  const afterLoadIdle = (fn) => {
    const run = () => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 200));
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  };
  const money = (value) => `PKR ${new Intl.NumberFormat('en-PK', { maximumFractionDigits: 0 }).format(value)}`;
  const roundUp = (value, increment) => Math.ceil((value - 0.000001) / increment) * increment;
  const roundDown = (value, increment) => Math.floor((value + 0.000001) / increment) * increment;
  const nearestStep = (value, step = 50) => Math.round(Number(value) / step) * step;
  const formatMetres = (value) => Number(value.toFixed(2)).toString();
  const setActionLabel = (button, label) => {
    const text = button.querySelector?.('[data-look-action-label]');
    if (text) text.textContent = label;
    else button.textContent = label;
  };
  const isStandardCut = (metres, standardMetres) => Math.abs(metres - standardMetres) < 0.000001;
  /* Policy: listed retail rate/m comes from wholesale × 1.55 (rounded up to the
     closer of 50 or 100). Standard cuts use listed SK/WC (ceil to next PKR 5 of
     rate×4.25 / rate×1.25 — no decimals). Custom lengths = rate × metres,
     nearest PKR 50. */
  const qualitySuitPrice = (option) => {
    const suit = Number(option?.dataset?.standardSuitPrice);
    return Number.isFinite(suit) && suit > 0 ? suit : 0;
  };
  const qualityWaistcoatPrice = (option) => {
    const waistcoat = Number(option?.dataset?.standardWaistcoatPrice);
    return Number.isFinite(waistcoat) && waistcoat > 0 ? waistcoat : 0;
  };
  const qualityRate = (option) => {
    const listed = Number(option?.dataset?.rate);
    if (Number.isFinite(listed) && listed > 0) return listed;
    const suit = qualitySuitPrice(option);
    if (suit > 0) return suit / 4.25;
    return NaN;
  };
  const isCollectionWc = (option) => option?.dataset?.wcSource === 'collection';
  const isPreviewOnlyWc = (option) => option?.dataset?.previewOnly === 'true';
  const isApprovedDesignOverlay = (overlay) => Boolean(
    overlay && overlay.approved === true && overlay.preview_only !== true &&
    overlay.src && Number.isFinite(Number(overlay.w)) && Number(overlay.w) > 0 &&
    Number.isFinite(Number(overlay.h)) && Number(overlay.h) > 0
  );
  const isReviewPreviewOverlay = (overlay) => Boolean(
    overlay && overlay.preview_only === true && overlay.approved !== true &&
    overlay.src && Number.isFinite(Number(overlay.w)) && Number(overlay.w) > 0 &&
    Number.isFinite(Number(overlay.h)) && Number(overlay.h) > 0
  );
  const setImageCors = (img, src) => {
    if (new URL(src, location.href).origin === location.origin) img.removeAttribute('crossorigin');
    else img.crossOrigin = 'anonymous';
  };

  const priceForQualityCut = (option, metres, { role } = {}) => {
    if (role === 'kameez' && isStandardCut(metres, 4.25)) {
      const suit = qualitySuitPrice(option);
      if (suit > 0) return suit;
    }
    if (role === 'waistcoat' && isStandardCut(metres, 1.25)) {
      const waistcoat = qualityWaistcoatPrice(option);
      if (waistcoat > 0) return waistcoat;
    }
    if (isCollectionWc(option)) {
      const rate = Number(option.dataset.rate) || 1000;
      if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(metres)) return NaN;
      return nearestStep(metres * rate, 50);
    }
    const rate = qualityRate(option);
    if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(metres)) return NaN;
    return nearestStep(metres * rate, 50);
  };

  const activeWcSelect = (studio) => {
    const source = studio.dataset.wcSource || 'matching';
    const design = studio.querySelector('[data-look-waistcoat-design]');
    return source === 'collection' && design?.options?.length
      ? design : studio.querySelector('[data-look-waistcoat]');
  };

  const setWcSource = (studio, source) => {
    const next = source === 'collection' ? 'collection' : 'matching';
    const prev = studio.dataset.wcSource || 'matching';
    if (prev === 'matching' && next === 'collection') {
      studio.saraWcMatchingValue = studio.querySelector('[data-look-waistcoat]')?.value;
    }
    studio.dataset.wcSource = next;
    studio.querySelectorAll('[data-look-wc-source]').forEach((btn) => {
      const on = btn.dataset.lookWcSource === next;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-selected', String(on));
      btn.tabIndex = on ? 0 : -1;
    });
    studio.querySelectorAll('[data-look-wc-panel-matching]').forEach((el) => { el.hidden = next !== 'matching'; });
    studio.querySelectorAll('[data-look-wc-panel-collection]').forEach((el) => { el.hidden = next !== 'collection'; });
    studio.querySelectorAll('[data-look-wc-collection-rate]').forEach((el) => { el.hidden = next !== 'collection'; });
    if (next === 'matching' && studio.saraWcMatchingValue) {
      const matching = studio.querySelector('[data-look-waistcoat]');
      if (matching && [...matching.options].some((o) => o.value === studio.saraWcMatchingValue)) {
        matching.value = studio.saraWcMatchingValue;
      }
    }
    const designSelect = studio.querySelector('[data-look-waistcoat-design]');
    const empty = studio.querySelectorAll('[data-look-wc-design-empty]');
    const hasDesigns = Boolean(designSelect?.options?.length);
    empty.forEach((el) => { el.hidden = !(next === 'collection' && !hasDesigns); });
    if (next === 'collection' && hasDesigns) {
      requestAnimationFrame(() => scrollSelectedSwatch(studio.querySelector('[data-look-waistcoat-design-palette]'), 'auto'));
    }
  };
  const readI18n = (studio) => {
    if (studio.saraI18n) return studio.saraI18n;
    const node = studio.querySelector('[data-sara-look-i18n]') || document.querySelector('[data-sara-look-i18n]');
    try { studio.saraI18n = node ? JSON.parse(node.textContent) : {}; }
    catch { studio.saraI18n = {}; }
    return studio.saraI18n;
  };
  const t = (studio, key, fallback = '', vars = {}) => {
    let value = readI18n(studio)[key] || fallback || key;
    for (const [name, replacement] of Object.entries(vars)) {
      value = value.replaceAll(`{{ ${name} }}`, String(replacement)).replaceAll(`{{${name}}}`, String(replacement));
    }
    return value;
  };

  const openScan = (studio, src, label, opener, event) => {
    const overlay = studio.querySelector('[data-look-scan-dialog]');
    const large = overlay?.querySelector('[data-look-scan-large]');
    const caption = overlay?.querySelector('[data-look-scan-caption]');
    const close = overlay?.querySelector('[data-look-scan-close]');
    if (!overlay || !large || !caption || !close || !src) return;
    large.src = src;
    large.alt = t(studio, 'fabric_image_alt', `${label} fabric image, enlarged`, { label });
    caption.textContent = t(studio, 'fabric_closeup', `${label} · Close-up of the actual fabric. Screen colour may differ from the cloth.`, { label });
    studio.saraScanOpener = opener;
    const hover = studio.querySelector('[data-look-scan-hover]');
    if (hover) hover.hidden = true;
    overlay.hidden = false;
    document.documentElement.classList.add('sf-fabric-zoom-lock');
    studio.saraFabricZoom = window.SaraFabricZoom?.attach?.(large) || null;
    studio.saraFabricZoom?.reset?.();
    close.focus?.();
    event.preventDefault();
  };

  const showScanHover = (studio, src, label) => {
    const hover = studio.querySelector('[data-look-scan-hover]');
    const image = hover?.querySelector('[data-look-scan-hover-image]');
    const caption = hover?.querySelector('[data-look-scan-hover-caption]');
    if (!hover || !image || !caption || !src || !studio.querySelector('[data-look-scan-dialog]')?.hidden) return;
    image.src = src;
    image.alt = t(studio, 'fabric_hover_alt', `${label} fabric close-up`, { label });
    caption.textContent = label;
    hover.hidden = false;
  };

  const hideScanHover = (studio) => {
    const hover = studio.querySelector('[data-look-scan-hover]');
    if (hover) hover.hidden = true;
  };

  const closeScan = (studio) => {
    const overlay = studio.querySelector('[data-look-scan-dialog]');
    if (!overlay || overlay.hidden) return;
    studio.saraFabricZoom?.reset?.();
    overlay.hidden = true;
    document.documentElement.classList.remove('sf-fabric-zoom-lock');
    studio.saraScanOpener?.focus?.();
  };

  const updateSwatchWheel = (host) => {
    if (!host) return null;
    const hostRect = host.getBoundingClientRect();
    if (!hostRect.width) return null;
    const center = hostRect.left + hostRect.width / 2;
    let nearest = null;
    let nearestDist = Infinity;
    host.querySelectorAll('.sf-look-studio__scan-tile').forEach((tile) => {
      const rect = tile.getBoundingClientRect();
      const tileCenter = rect.left + rect.width / 2;
      const norm = Math.max(-1, Math.min(1, (tileCenter - center) / Math.max(hostRect.width * 0.5, 1)));
      const abs = Math.abs(norm);
      const scale = 1.08 - abs * 0.34;
      const rotateY = norm * -42;
      const lift = (1 - abs) * 18;
      const opacity = 1 - abs * 0.42;
      tile.style.transform = `translateZ(${lift}px) rotateY(${rotateY}deg) scale(${scale})`;
      tile.style.opacity = String(Math.max(0.42, opacity));
      tile.style.zIndex = String(Math.round((1 - abs) * 20));
      tile.classList.toggle('is-wheel-center', abs < 0.18);
      if (abs < nearestDist) {
        nearestDist = abs;
        nearest = tile;
      }
    });
    if (nearest) {
      host.querySelectorAll('.sf-look-studio__scan-tile.is-wheel-center').forEach((tile) => {
        if (tile !== nearest) tile.classList.remove('is-wheel-center');
      });
      nearest.classList.add('is-wheel-center');
    }
    return nearest;
  };

  /* Centre tile drives the try-on: no second tap needed while scrolling the wheel. */
  const applyCentreSwatch = (host, { snap = false } = {}) => {
    if (!host || host.dataset.sfProgrammaticScroll === '1' || Date.now() < (host.saraClickLockUntil || 0)) return;
    const select = host.saraSelect;
    const nearest = updateSwatchWheel(host);
    const button = nearest?.querySelector?.('.sf-look-studio__scan');
    const index = Number(button?.dataset?.index);
    if (!select || !Number.isInteger(index) || index < 0 || index >= select.options.length) return;
    if (select.selectedIndex !== index) {
      /* Mark index first so update() does not fight the user's scroll with scrollIntoView. */
      host.dataset.sfSelectedIndex = String(index);
      select.selectedIndex = index;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (snap && nearest?.scrollIntoView) {
      const hostRect = host.getBoundingClientRect();
      const tileRect = nearest.getBoundingClientRect();
      const offset = Math.abs((tileRect.left + tileRect.width / 2) - (hostRect.left + hostRect.width / 2));
      if (offset > 10) {
        host.dataset.sfProgrammaticScroll = '1';
        nearest.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        window.setTimeout(() => {
          delete host.dataset.sfProgrammaticScroll;
          updateSwatchWheel(host);
        }, 280);
      }
    }
  };

  const syncSwatchNav = (host) => {
    const carousel = host?.closest?.('[data-look-swatch-carousel]');
    if (!host || !carousel) return;
    const prev = carousel.querySelector('[data-look-swatch-prev]');
    const next = carousel.querySelector('[data-look-swatch-next]');
    const max = Math.max(0, host.scrollWidth - host.clientWidth);
    const overflow = max > 4;
    const atStart = host.scrollLeft <= 2;
    const atEnd = host.scrollLeft >= max - 2;
    carousel.classList.toggle('is-scrollable', overflow);
    if (prev) prev.disabled = !overflow || atStart;
    if (next) next.disabled = !overflow || atEnd;
    updateSwatchWheel(host);
  };

  const scrollSelectedSwatch = (host, behavior = 'smooth') => {
    if (!host) return;
    const selected = host.querySelector('.sf-look-studio__scan[aria-pressed="true"]');
    const tile = selected?.closest('.sf-look-studio__scan-tile');
    if (tile?.scrollIntoView) {
      host.saraClickLockUntil = Date.now() + (behavior === 'smooth' ? 700 : 240);
      host.dataset.sfProgrammaticScroll = '1';
      tile.scrollIntoView({ inline: 'center', block: 'nearest', behavior });
      window.setTimeout(() => {
        delete host.dataset.sfProgrammaticScroll;
        syncSwatchNav(host);
      }, behavior === 'smooth' ? 650 : 180);
    }
    requestAnimationFrame(() => syncSwatchNav(host));
  };

  const stepSwatchWheel = (host, dir) => {
    const tiles = [...host.querySelectorAll('.sf-look-studio__scan-tile')];
    if (!tiles.length) return;
    const hostRect = host.getBoundingClientRect();
    const center = hostRect.left + hostRect.width / 2;
    let index = 0;
    let best = Infinity;
    tiles.forEach((tile, i) => {
      const rect = tile.getBoundingClientRect();
      const dist = Math.abs(rect.left + rect.width / 2 - center);
      if (dist < best) { best = dist; index = i; }
    });
    const next = tiles[Math.max(0, Math.min(tiles.length - 1, index + dir))];
    if (!next) return;
    host.dataset.sfProgrammaticScroll = '1';
    next.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    window.setTimeout(() => {
      delete host.dataset.sfProgrammaticScroll;
      applyCentreSwatch(host);
    }, 550);
  };

  const ensureSwatchCarousel = (studio, host) => {
    if (host.closest('[data-look-swatch-carousel]')) return;
    const carousel = document.createElement('div');
    carousel.className = 'sf-look-studio__swatch-carousel';
    carousel.dataset.lookSwatchCarousel = '';

    const makeNav = (dir, key, fallback, attr) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `sf-look-studio__swatch-nav sf-look-studio__swatch-nav--${dir}`;
      button.setAttribute(attr, '');
      button.setAttribute('aria-label', t(studio, key, fallback));
      button.innerHTML = `<span aria-hidden="true">${dir === 'prev' ? '‹' : '›'}</span>`;
      button.addEventListener('click', () => {
        const rtl = getComputedStyle(host).direction === 'rtl';
        const sign = (dir === 'prev' ? -1 : 1) * (rtl ? -1 : 1);
        stepSwatchWheel(host, sign);
      });
      return button;
    };

    const prev = makeNav('prev', 'swatch_prev', 'Previous shades', 'data-look-swatch-prev');
    const next = makeNav('next', 'swatch_next', 'Next shades', 'data-look-swatch-next');
    host.parentNode.insertBefore(carousel, host);
    carousel.append(prev, host, next);
    host.addEventListener('scroll', () => {
      syncSwatchNav(host);
      applyCentreSwatch(host);
      window.clearTimeout(host.saraWheelSnapTimer);
      host.saraWheelSnapTimer = window.setTimeout(() => applyCentreSwatch(host, { snap: true }), 140);
    }, { passive: true });
    if (!studio.saraSwatchResizeBound) {
      studio.saraSwatchResizeBound = true;
      window.addEventListener('resize', () => {
        studio.querySelectorAll('[data-look-kameez-palette], [data-look-waistcoat-palette], [data-look-waistcoat-design-palette]').forEach(syncSwatchNav);
      }, { passive: true });
    }
  };

  const renderPalette = (studio, select, selector) => {
    const host = studio.querySelector(selector);
    if (!host || !select?.options) return;
    const isDesignPalette = select.hasAttribute('data-look-waistcoat-design');
    ensureSwatchCarousel(studio, host);
    host.saraSelect = select;
    host.replaceChildren(...[...select.options].map((option, index) => {
      const tile = document.createElement('div');
      tile.className = isDesignPalette ? 'sf-look-studio__scan-tile sf-look-studio__design-tile' : 'sf-look-studio__scan-tile';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sf-look-studio__scan';
      button.dataset.index = String(index);
      button.title = option.value;
      button.setAttribute('aria-label', t(studio, 'choose_fabric', `Choose ${option.value}`, { label: option.value }));
      button.setAttribute('aria-pressed', String(option.selected));
      const image = document.createElement('span');
      image.className = 'sf-look-studio__scan-image';
      const colour = option.dataset.colour || '#777';
      image.style.backgroundColor = colour;
      const thumbnail = option.dataset.thumb || option.dataset.scan;
      if (thumbnail) {
        image.style.backgroundImage = `url("${thumbnail}")`;
      } else {
        image.style.backgroundImage = `linear-gradient(145deg, ${colour} 0%, ${colour} 55%, #00000022 100%)`;
      }
      const label = document.createElement('span');
      label.textContent = isDesignPalette
        ? `${option.dataset.quality || ''} · ${option.dataset.shade || option.value}`
        : (option.dataset.shade?.toLowerCase().startsWith('representative') ? option.dataset.quality : (option.dataset.shade || option.value));
      const skOk = option.dataset.skAvailable === 'true' && Number(option.dataset.skStock) > 0;
      const wcOk = option.dataset.wcAvailable === 'true' && Number(option.dataset.wcStock) > 0;
      const designPriceOk = option.dataset.priceOk === 'true';
      const previewOnly = isPreviewOnlyWc(option);
      const soldOut = isDesignPalette ? !wcOk && !previewOnly : studio.dataset.devCheckout === 'true' && !skOk && !wcOk;
      if (soldOut) {
        tile.classList.add('is-sold-out');
        button.classList.add('is-sold-out');
        const badge = document.createElement('span');
        badge.className = 'sf-look-studio__sold-out-badge';
        badge.textContent = isDesignPalette && !designPriceOk
          ? t(studio, 'wc_design_unavailable', 'Unavailable')
          : t(studio, 'shade_sold_out_badge', 'Sold out');
        button.append(image, label, badge);
        button.setAttribute('aria-label', t(studio, 'choose_fabric_sold_out', `Sold out: ${option.value}`, { label: option.value }));
      } else {
        button.append(image, label);
      }
      if (isDesignPalette) {
        const price = document.createElement('small');
        price.textContent = previewOnly
          ? t(studio, 'wc_preview_only', 'Coming soon · Rs 1,000/m')
          : designPriceOk
            ? t(studio, 'wc_design_cut_price', '1.25 m cut · Rs 1,250')
            : t(studio, 'wc_design_price_pending', 'Price being confirmed');
        button.appendChild(price);
      }
      button.addEventListener('click', () => {
        host.dataset.sfSelectedIndex = String(index);
        select.selectedIndex = index;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        scrollSelectedSwatch(host, 'instant');
      });
      tile.appendChild(button);
      return tile;
    }));
    host.dataset.sfSelectedIndex = String(select.selectedIndex);
    if (!host.closest('[hidden]')) scrollSelectedSwatch(host, 'auto');
    requestAnimationFrame(() => updateSwatchWheel(host));
  };

  const quoteUrl = (baseHref, lines) => {
    const url = new URL(baseHref);
    url.searchParams.set('text', lines.join('\n'));
    return url.toString();
  };

  const lookKey = (look) => JSON.stringify([
    look.pieces, look.custom, look.kValue, look.wValue, look.wcSource || 'matching',
    look.kCount, look.wCount, look.kMetres, look.wMetres
  ]);

  const looksStorageKey = (studio) =>
    `sf-looks:${studio.dataset.focusQuality || 'all'}:${studio.dataset.devCheckout || 'false'}`;

  const persistLooks = (studio) => {
    try {
      const looks = (studio.saraSavedLooks || []).map((look) => ({
        pieces: look.pieces,
        custom: look.custom,
        kValue: look.kValue,
        wValue: look.wValue,
        wcSource: look.wcSource || 'matching',
        designHandle: look.designHandle || '',
        kCount: look.kCount,
        wCount: look.wCount,
        kMetres: look.kMetres,
        wMetres: look.wMetres,
        summary: look.summary,
        total: look.total,
        photoIsScan: Boolean(look.photoIsScan),
        cartItems: Array.isArray(look.cartItems)
          ? look.cartItems.map(({ stock, ...item }) => item)
          : null
      }));
      localStorage.setItem(looksStorageKey(studio), JSON.stringify(looks));
    } catch { /* private mode / quota */ }
  };

  /** Normalize em/en dashes and spacing so saved looks still match option values. */
  const normShadeKey = (value) => String(value || '')
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  /** Match a saved look label (full value / shade / pool key) to a <option>. */
  const optionMatchesSavedValue = (option, value) => {
    if (!option || value == null || value === '') return false;
    if (option.value === value) return true;
    const target = normShadeKey(value);
    if (!target) return false;
    if (normShadeKey(option.value) === target) return true;
    if (normShadeKey(option.dataset.shadeId || '') === target) return true;
    if (normShadeKey(option.dataset.shade || '') === target) return true;
    if (normShadeKey(option.dataset.poolKey || '') === target) return true;
    const quality = option.dataset.quality || '';
    const shade = option.dataset.shade || '';
    if (quality && shade && normShadeKey(`${quality} - ${shade}`) === target) return true;
    // Saved as "Shade" only, or "Quality - Shade" with a different dash.
    const shadePart = normShadeKey(String(value).split(/[\u2010-\u2015\u2212-]/).slice(-1)[0]);
    if (shadePart && normShadeKey(shade) === shadePart) {
      const qualityPart = normShadeKey(String(value).split(/[\u2010-\u2015\u2212-]/)[0]);
      if (!qualityPart || !quality || qualityPart === normShadeKey(quality) || target.includes(normShadeKey(quality))) {
        return true;
      }
    }
    return false;
  };

  const findShadeOption = (studio, value, { collection = false } = {}) => {
    if (!value) return null;
    const lists = collection
      ? [studio.querySelector('[data-look-waistcoat-design]')]
      : [studio.querySelector('[data-look-kameez]'), studio.querySelector('[data-look-waistcoat]')];
    for (const sel of lists) {
      const opt = [...(sel?.options || [])].find((o) => optionMatchesSavedValue(o, value));
      if (opt) return opt;
    }
    return null;
  };

  /** Set a <select> from a saved look value; returns true when a real option is selected. */
  const setSelectToSavedValue = (select, value) => {
    if (!select) return false;
    if (value != null && value !== '') {
      const match = [...select.options].find((o) => optionMatchesSavedValue(o, value));
      if (match) {
        select.value = match.value;
        return select.selectedOptions.length > 0;
      }
    }
    return select.selectedOptions.length > 0;
  };

  const ensureSelectHasOption = (select) => {
    if (!select) return false;
    if (select.selectedOptions.length > 0) return true;
    if (select.options.length > 0) {
      select.selectedIndex = 0;
      return select.selectedOptions.length > 0;
    }
    return false;
  };

  const buildCutItemFromOption = (option, piece, metres, count) => {
    if (!option || !count) return null;
    const isSk = piece !== 'waistcoat';
    const id = Number(isSk ? option.dataset.skVariantId : option.dataset.wcVariantId);
    const variantPrice = Number(isSk ? option.dataset.skPriceCents : option.dataset.wcPriceCents);
    const stock = Number(isSk ? option.dataset.skStock : option.dataset.wcStock);
    const availableKey = isSk ? 'skAvailable' : 'wcAvailable';
    const unitPrice = priceForQualityCut(option, metres, { role: isSk ? 'kameez' : 'waistcoat' });
    if (!Number.isSafeInteger(id) || id <= 0) return null;
    if (!Number.isFinite(unitPrice) || unitPrice <= 0) return null;
    if (variantPrice !== unitPrice * 100) return null;
    if (piece === 'waistcoat' && isCollectionWc(option) && option.dataset.priceOk !== 'true') return null;
    const shortStock = !Number.isInteger(stock) || stock < count;
    const markedUnavailable = option.dataset[availableKey] !== 'true';
    const preorder = markedUnavailable || shortStock;
    const cm = Math.round(metres * 100);
    const poolCm = Number(option.dataset.poolCm);
    const poolKey = option.dataset.poolKey || `${option.dataset.quality}|${option.dataset.shadeId || option.dataset.shade}`;
    const properties = {
      Fabric: option.dataset.quality,
      Shade: option.dataset.shade,
      Cut: `${formatMetres(metres)} m`,
      _piece: piece,
      ...(preorder ? { Fulfilment: 'Pre-order — upcoming stock' } : {})
    };
    if (piece === 'waistcoat' && isCollectionWc(option)) {
      properties['Waistcoat source'] = 'Design collection';
      properties.Design = option.dataset.designHandle || option.value;
      properties['Source quality'] = option.dataset.quality || '';
    }
    return {
      id,
      quantity: count,
      stock: Number.isInteger(stock) ? stock : 0,
      cm: isCollectionWc(option) ? undefined : cm,
      poolCm: isCollectionWc(option) ? undefined : (Number.isFinite(poolCm) && poolCm > 0 ? poolCm : undefined),
      poolKey: isCollectionWc(option) ? undefined : poolKey,
      properties
    };
  };

  const syncLookStock = (studio, look) => {
    if (look.custom) {
      look.cartItems = null;
      look.cartBlockReason = look.cartBlockReason || '';
      return;
    }
    const kOpt = findShadeOption(studio, look.kValue);
    const wOpt = look.wcSource === 'collection'
      ? findShadeOption(studio, look.wValue, { collection: true })
      : findShadeOption(studio, look.wValue);
    const needK = look.pieces !== 'waistcoat' && (look.kCount || 0) > 0;
    const needW = look.pieces !== 'suit' && (look.wCount || 0) > 0;
    const items = [];
    if (needK) {
      const item = buildCutItemFromOption(kOpt, 'shalwar kameez', Number(look.kMetres) || 4.25, Number(look.kCount) || 0);
      if (item) items.push(item);
    }
    if (needW) {
      const item = buildCutItemFromOption(wOpt, 'waistcoat', Number(look.wMetres) || 1.25, Number(look.wCount) || 0);
      if (item) items.push(item);
    }
    const expected = Number(needK) + Number(needW);
    if (items.length === expected && expected > 0) {
      look.cartItems = items;
      look.preorder = items.some((item) => item.properties?.Fulfilment === 'Pre-order — upcoming stock');
      look.cartBlockReason = '';
      return;
    }
    /* Keep prior cartItems only if still price-valid; otherwise clear for tray rebuild. */
    if (Array.isArray(look.cartItems) && look.cartItems.length) {
      const options = [
        ...(studio.querySelector('[data-look-kameez]')?.options || []),
        ...(studio.querySelector('[data-look-waistcoat]')?.options || []),
        ...(studio.querySelector('[data-look-waistcoat-design]')?.options || [])
      ];
      look.cartItems = look.cartItems.map((item) => {
        const opt = options.find((o) =>
          Number(o.dataset.skVariantId) === item.id || Number(o.dataset.wcVariantId) === item.id
        );
        if (!opt) return { ...item, stock: 0 };
        const isSk = Number(opt.dataset.skVariantId) === item.id;
        const available = (isSk ? opt.dataset.skAvailable : opt.dataset.wcAvailable) === 'true';
        const stock = Number(isSk ? opt.dataset.skStock : opt.dataset.wcStock);
        const expectedPrice = priceForQualityCut(opt, isSk ? 4.25 : 1.25, { role: isSk ? 'kameez' : 'waistcoat' });
        const variantPrice = Number(isSk ? opt.dataset.skPriceCents : opt.dataset.wcPriceCents);
        const priceOk = Number.isFinite(expectedPrice) && expectedPrice > 0 && variantPrice === expectedPrice * 100 &&
          (isSk || !isCollectionWc(opt) || opt.dataset.priceOk === 'true');
        return { ...item, stock: available && priceOk && Number.isFinite(stock) ? stock : 0 };
      });
      if (look.cartItems.every((item) => item.stock > 0 || item.properties?.Fulfilment)) return;
    }
    look.cartItems = null;
  };

  const restoreLooks = (studio) => {
    try {
      const raw = localStorage.getItem(looksStorageKey(studio));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || !parsed.length) return;
      studio.saraSavedLooks = parsed.slice(0, 20).map((look) => {
        const next = {
          pieces: look.pieces || 'both',
          custom: Boolean(look.custom),
          kValue: look.kValue || '',
          wValue: look.wValue || '',
          wcSource: look.wcSource === 'collection' ? 'collection' : 'matching',
          designHandle: look.designHandle || '',
          kCount: Number(look.kCount) || 0,
          wCount: Number(look.wCount) || 0,
          kMetres: Number(look.kMetres) || 4.25,
          wMetres: Number(look.wMetres) || 1.25,
          summary: look.summary || '',
          total: Number(look.total) || 0,
          photo: null,
          photoIsScan: Boolean(look.photoIsScan),
          cartItems: Array.isArray(look.cartItems) ? look.cartItems : null
        };
        syncLookStock(studio, next);
        return next;
      });
    } catch { /* ignore corrupt storage */ }
  };

  const normalizeShadeSlug = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^shade-/, '')
    .replace(/-/g, '_');

  const optionMatchesShade = (option, slug) => {
    if (!option || !slug) return false;
    const id = normalizeShadeSlug(option.dataset.shadeId);
    const label = normalizeShadeSlug(option.dataset.shade);
    const alt = slug === 'charcoal_grey' ? 'charcool_grey'
      : slug === 'charcool_grey' ? 'charcoal_grey' : null;
    return id === slug || label === slug || (alt && (id === alt || label === alt));
  };

  const applyShadeFromUrl = (studio) => {
    const params = new URLSearchParams(location.search);
    const fromQuery = params.get('shade') || params.get('tile_thumb');
    const fromHash = location.hash.startsWith('#shade-') ? location.hash.slice(1) : '';
    const slug = normalizeShadeSlug(fromQuery || fromHash);
    if (!slug) return false;
    let matched = false;
    [['[data-look-kameez]', '[data-look-kameez-palette]'],
      ['[data-look-waistcoat]', '[data-look-waistcoat-palette]']].forEach(([selectSel]) => {
      const select = studio.querySelector(selectSel);
      if (!select?.options) return;
      const index = [...select.options].findIndex((option) => optionMatchesShade(option, slug));
      if (index < 0) return;
      select.selectedIndex = index;
      matched = true;
    });
    return matched;
  };

  const cartRoots = () => {
    const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    return {
      root,
      cart: `${root}cart`,
      checkout: `${root}checkout`,
      add: `${root}cart/add.js`
    };
  };

  const replaceSectionHtml = (id, html) => {
    const el = document.getElementById(id);
    if (!el || !html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.querySelector('.shopify-section') || doc.body;
    el.innerHTML = section.innerHTML;
  };

  const ensureCartToast = (studio) => {
    let toast = studio.querySelector('[data-look-cart-toast]');
    if (toast) return toast;
    toast = document.createElement('div');
    toast.className = 'sf-look-studio__cart-toast';
    toast.hidden = true;
    toast.dataset.lookCartToast = '';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    const routes = cartRoots();
    toast.innerHTML = `
      <div class="sf-look-studio__cart-toast-inner">
        <p class="sf-look-studio__cart-toast-msg" data-look-cart-toast-msg></p>
        <div class="sf-look-studio__cart-toast-actions">
          <a class="sf-look-studio__cart-toast-link" data-look-cart-toast-cart href="${routes.cart}">${t(studio, 'cart_toast_view', 'View cart')}</a>
          <a class="sf-look-studio__cart-toast-link sf-look-studio__cart-toast-link--primary" data-look-cart-toast-checkout href="${routes.checkout}">${t(studio, 'cart_toast_checkout', 'Checkout')}</a>
          <button type="button" class="sf-look-studio__cart-toast-close" data-look-cart-toast-close>${t(studio, 'cart_toast_continue', 'Continue')}</button>
        </div>
      </div>`;
    toast.querySelector('[data-look-cart-toast-close]')?.addEventListener('click', () => {
      toast.hidden = true;
    });
    studio.appendChild(toast);
    return toast;
  };

  const showCartToast = (studio, { count, lookWord }) => {
    const toast = ensureCartToast(studio);
    const msg = toast.querySelector('[data-look-cart-toast-msg]');
    if (msg) {
      msg.textContent = t(
        studio,
        'cart_toast_added',
        `${count} standard ${lookWord} added to your cart.`,
        { count, look_word: lookWord }
      );
    }
    const routes = cartRoots();
    const cartLink = toast.querySelector('[data-look-cart-toast-cart]');
    const checkoutLink = toast.querySelector('[data-look-cart-toast-checkout]');
    if (cartLink) cartLink.href = routes.cart;
    if (checkoutLink) checkoutLink.href = routes.checkout;
    toast.hidden = false;
    toast.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const closeHelpDialog = (studio) => {
    const dialog = studio.querySelector('[data-look-help-dialog]');
    if (!dialog) return;
    dialog.hidden = true;
    const opener = studio.saraHelpOpener;
    studio.saraHelpOpener = null;
    if (opener && typeof opener.focus === 'function') opener.focus();
  };

  const ensureHelpDialog = (studio) => {
    let dialog = studio.querySelector('[data-look-help-dialog]');
    if (dialog) return dialog;
    dialog = document.createElement('div');
    dialog.className = 'sf-look-studio__help-dialog';
    dialog.hidden = true;
    dialog.dataset.lookHelpDialog = '';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'sf-look-help-title');
    dialog.innerHTML = `
      <div class="sf-look-studio__help-dialog-inner">
        <h3 id="sf-look-help-title" class="sf-look-studio__help-dialog-title" data-look-help-title></h3>
        <p class="sf-look-studio__help-dialog-msg" data-look-help-msg></p>
        <div class="sf-look-studio__help-dialog-actions">
          <a class="sf-look-studio__help-dialog-wa" data-look-help-wa href="#" target="_blank" rel="noopener noreferrer"></a>
          <button type="button" class="sf-look-studio__help-dialog-close" data-look-help-close></button>
        </div>
      </div>`;
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeHelpDialog(studio);
    });
    dialog.querySelector('[data-look-help-close]')?.addEventListener('click', () => closeHelpDialog(studio));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !dialog.hidden) closeHelpDialog(studio);
    });
    studio.appendChild(dialog);
    return dialog;
  };

  const showHelpDialog = (studio, { reason, href, opener } = {}) => {
    const dialog = ensureHelpDialog(studio);
    const title = dialog.querySelector('[data-look-help-title]');
    const msg = dialog.querySelector('[data-look-help-msg]');
    const wa = dialog.querySelector('[data-look-help-wa]');
    const closeBtn = dialog.querySelector('[data-look-help-close]');
    const isConflict = reason === 'conflict';
    if (title) {
      title.textContent = isConflict
        ? t(studio, 'help_title_conflict', 'These looks cannot go to cart yet')
        : t(studio, 'help_title_quote', 'These looks need a quote');
    }
    if (msg) {
      msg.textContent = isConflict
        ? t(studio, 'help_msg_conflict', 'The saved looks need more fabric than is available for a shared shade, or exceed a cut limit. Change a look, or send the details on WhatsApp so we can help.')
        : t(studio, 'help_msg_quote', 'Custom lengths or unavailable cuts cannot go straight to the cart. Send the details on WhatsApp for a manual quote.');
    }
    if (wa) {
      wa.href = href || studio.saraEnquiryHref || wa.dataset.baseHref || '#';
      wa.textContent = t(studio, 'help_wa', 'Message on WhatsApp');
    }
    if (closeBtn) closeBtn.textContent = t(studio, 'help_close', 'Keep editing');
    studio.saraHelpOpener = opener || null;
    dialog.hidden = false;
    (wa || closeBtn)?.focus?.();
  };

  const renderTray = (studio) => {
    const list = studio.querySelector('[data-look-tray-list]');
    if (!list) return;
    const looks = studio.saraSavedLooks || [];
    const empty = studio.querySelector('[data-look-tray-empty]');
    const footer = studio.querySelector('[data-look-tray-footer]');
    const count = studio.querySelector('[data-look-tray-count]');
    const total = studio.querySelector('[data-look-tray-total]');
    const note = studio.querySelector('[data-look-tray-note]');
    const cart = studio.querySelector('[data-look-cart]');
    const enquiry = studio.querySelector('[data-look-tray-enquire]');
    looks.forEach((look) => syncLookStock(studio, look));
    count.textContent = `(${looks.length})`;
    empty.hidden = looks.length > 0;
    footer.hidden = looks.length === 0;
    list.replaceChildren();
    if (!looks.length) {
      studio.saraCartItems = null;
      if (cart) cart.hidden = true;
      /* Leave [data-look-enquire] alone — update() sets it from cart eligibility. */
      persistLooks(studio);
      return;
    }

    const allLines = [
      t(studio, 'wa_intro', 'Hello Sara Fabrics, please check whether the selected unstitched fabrics below are currently available before I place an order:'),
      '',
      t(studio, 'wa_heading', 'SELECTED FABRIC ORDER ENQUIRY')
    ];
    let allTotal = 0;
    const cartItems = [];
    let quoteOnly = 0;
    const variantCounts = new Map();
    const variantStock = new Map();
    const poolUsageCm = new Map();
    const poolCapCm = new Map();
    looks.forEach((look, index) => {
      allTotal += look.total;
      allLines.push(t(studio, 'look_line', `${index + 1}. Look ${index + 1}: ${look.summary}; ${money(look.total)}; ${look.custom ? 'custom' : 'standard'} cut`, {
        n: index + 1,
        summary: look.summary,
        amount: money(look.total),
        cut: look.custom ? t(studio, 'wa_cut_custom_short', 'custom') : t(studio, 'wa_cut_standard_short', 'standard')
      }));
      if (look.cartItems) {
        look.cartItems.forEach((item) => {
          variantCounts.set(item.id, (variantCounts.get(item.id) || 0) + item.quantity);
          variantStock.set(item.id, Math.min(variantStock.get(item.id) ?? Infinity, item.stock));
          if (item.poolKey && Number.isFinite(item.cm) && item.cm > 0) {
            poolUsageCm.set(item.poolKey, (poolUsageCm.get(item.poolKey) || 0) + (item.quantity * item.cm));
            if (Number.isFinite(item.poolCm) && item.poolCm > 0) {
              poolCapCm.set(item.poolKey, Math.min(poolCapCm.get(item.poolKey) ?? Infinity, item.poolCm));
            }
          }
          const { stock, poolKey, poolCm, cm, ...cartItem } = item;
          cartItems.push({ ...cartItem, properties: { ...cartItem.properties, Look: `Look ${index + 1}` } });
        });
      } else quoteOnly += 1;

      if (!look.photo) {
        const layerLook = layerLookFromSaved(studio, look);
        if (layerLook && studio.saraLayerPhoto && studio.saraLayerPhotoKey === layerPhotoKey(layerLook)) {
          look.photo = studio.saraLayerPhoto;
          look.photoIsScan = false;
        } else if (!layerLook) {
          const fallback = scanFallbackPhoto(studio, look);
          if (fallback) {
            look.photo = fallback;
            look.photoIsScan = true;
          }
        }
      }

      const card = document.createElement('li');
      card.className = 'sf-look-studio__look-card';
      if (look.photo) {
        const image = document.createElement('img');
        image.src = look.photo;
        image.alt = look.photoIsScan
          ? t(studio, 'look_photo_scan_alt', `Fabric image for look ${index + 1}; inspect both selected fabric swatches`, { n: index + 1 })
          : t(studio, 'look_photo_model_alt', `Appearance-matched styling render for look ${index + 1}; fabric swatches show the selected cloth`, { n: index + 1 });
        image.width = 90;
        image.height = 130;
        image.loading = 'lazy';
        card.appendChild(image);
      } else {
        /* Without a thumb the old 9rem image column still reserved space and
           squeezed title/summary into ~90px (one-word wrapping). */
        card.classList.add('sf-look-studio__look-card--no-photo');
      }
      const detail = document.createElement('div');
      detail.className = 'sf-look-studio__look-detail';
      const title = document.createElement('strong');
      title.textContent = t(studio, 'look_title', `Look ${index + 1} · ${money(look.total)}`, { n: index + 1, amount: money(look.total) });
      const description = document.createElement('p');
      description.textContent = look.summary;
      const status = document.createElement('small');
      const enquiryMode = studio.dataset.visualPreview === 'true' && studio.dataset.devCheckout !== 'true';
      const lookPreorder = Boolean(
        look.preorder
        || (look.cartItems || []).some((item) => item?.properties?.Fulfilment === 'Pre-order — upcoming stock')
      );
      status.textContent = enquiryMode
        ? t(studio, 'status_enquiry', 'Ready for WhatsApp enquiry')
        : (look.cartItems
          ? (lookPreorder
            ? t(studio, 'status_preorder', 'Standard cuts · pre-order / upcoming stock')
            : t(studio, 'status_standard', 'Standard cuts · can be added to cart while stock remains'))
          : (look.cartBlockReason
            ? t(studio, 'status_blocked', `Quote only · ${look.cartBlockReason}`, { reason: look.cartBlockReason })
            : t(studio, 'status_quote', 'Quote only · custom length or unavailable units')));
      detail.append(title, description, status);
      const actions = document.createElement('div');
      actions.className = 'sf-look-studio__look-actions';
      for (const [label, action] of [[t(studio, 'edit', 'Edit'), 'edit'], [t(studio, 'remove', 'Remove'), 'remove']]) {
        const button = document.createElement('button');
        button.type = 'button';
        const icon = action === 'remove' ? studio.querySelector('[data-look-icon-remove]')?.content?.cloneNode(true) : null;
        if (icon) button.appendChild(icon);
        const buttonLabel = document.createElement('span');
        buttonLabel.textContent = label;
        button.appendChild(buttonLabel);
        button.dataset.lookAction = action;
        button.dataset.lookIndex = String(index);
        actions.appendChild(button);
      }
      card.append(detail, actions);
      list.appendChild(card);
    });
    /* Async backfill for looks still missing a thumb (save raced compose / restore). */
    const trayGen = (studio.saraTrayPhotoGen = (studio.saraTrayPhotoGen || 0) + 1);
    looks.forEach((look) => {
      if (look.photo || look._photoPending) return;
      const layerLook = layerLookFromSaved(studio, look);
      if (!layerLook) return;
      look._photoPending = true;
      composeLayerPhoto(studio, layerLook).then((dataUrl) => {
        look._photoPending = false;
        if (studio.saraTrayPhotoGen !== trayGen) return;
        if (!(studio.saraSavedLooks || []).includes(look)) return;
        if (dataUrl) {
          look.photo = dataUrl;
          look.photoIsScan = false;
        } else {
          const fallback = scanFallbackPhoto(studio, look);
          if (fallback) {
            look.photo = fallback;
            look.photoIsScan = true;
          }
        }
        if (look.photo) renderTray(studio);
      });
    });
    allLines.push('', t(studio, 'fabric_total', `Fabric total: ${money(allTotal)}`, { amount: money(allTotal) }), t(studio, 'wa_outro_tray', 'Please confirm availability for each listed quality and shade, then confirm the exact shade, cutting, final price and delivery details. This message is an enquiry, not a confirmed order.'));
    total.textContent = t(studio, 'tray_total', `Saved-look fabric total: ${money(allTotal)}`, { amount: money(allTotal) });
    const waBase = enquiry?.dataset?.baseHref
      || studio.querySelector('[data-look-enquire]')?.dataset?.baseHref
      || '';
    const enquiryHref = waBase ? quoteUrl(waBase, allLines) : '';
    if (enquiry) enquiry.href = enquiryHref;
    studio.saraEnquiryHref = enquiryHref;
    const hasVariantConflict = [...variantCounts].some(([id, quantity]) => quantity > variantStock.get(id));
    const hasPoolConflict = [...poolUsageCm].some(([key, usedCm]) => {
      const cap = poolCapCm.get(key);
      return Number.isFinite(cap) && usedCm > cap;
    });
    const hasConflict = hasVariantConflict || hasPoolConflict;
    const checkoutHosts = new Set([
      'sara-fabrics-winter-2026-checkout-lab.myshopify.com',
      'shop.sarafabrics.com',
      'sarafabrics.myshopify.com'
    ]);
    const isLabHost = location.hostname === 'sara-fabrics-winter-2026-checkout-lab.myshopify.com';
    const safeStore = studio.dataset.devCheckout === 'true' && checkoutHosts.has(location.hostname);
    /* Standard cuts always use Add to cart. Short stock / shared-pool oversell is
       pre-order (upcoming stock) — never swap the button for a WhatsApp help CTA. */
    const preorder = hasConflict || cartItems.some((item) => item?.properties?.Fulfilment === 'Pre-order — upcoming stock');
    if (preorder) {
      cartItems.forEach((item) => {
        if (!item.properties) item.properties = {};
        if (!item.properties.Fulfilment) item.properties.Fulfilment = 'Pre-order — upcoming stock';
      });
    }
    const canAdd = safeStore && cartItems.length > 0;
    studio.saraCartItems = canAdd ? cartItems : null;
    studio.saraCartPreorder = Boolean(canAdd && preorder);
    if (cart) {
      cart.hidden = !canAdd;
      cart.disabled = Boolean(studio.saraSubmitting);
      cart.dataset.lookCartMode = 'add';
      cart.classList.remove('sf-look-studio__cart--help');
      cart.setAttribute?.('aria-disabled', 'false');
      if (canAdd) {
        const lookWord = looks.length - quoteOnly === 1
          ? t(studio, 'look_word_one', 'look')
          : t(studio, 'look_word_other', 'looks');
        const cartLabel = t(studio, 'cart_looks', `Add ${looks.length - quoteOnly} standard ${lookWord} to cart`, {
          count: looks.length - quoteOnly,
          look_word: lookWord
        });
        setActionLabel(cart, `${isLabHost ? 'DEV TEST · ' : ''}${cartLabel}`);
      }
    }
    if (enquiry) {
      if (studio.dataset.devCheckout === 'true') {
        enquiry.hidden = true;
      } else {
        enquiry.hidden = false;
        enquiry.classList.remove('sf-look-studio__enquire--secondary');
      }
    }
    const singleEnquire = studio.querySelector('[data-look-enquire]');
    /* Once looks are saved, the draft WhatsApp CTA stays hidden. */
    if (singleEnquire && looks.length > 0) singleEnquire.hidden = true;
    if (note) {
      note.textContent = hasConflict || studio.saraCartPreorder
        ? t(studio, 'preorder_note', 'Some cuts are short on current stock and will be taken as a pre-order for upcoming stock. You can still add them to the cart.')
        : quoteOnly
          ? (studio.dataset.devCheckout === 'true'
            ? (canAdd
              ? t(studio, 'quote_only', `${quoteOnly} look(s) need a manual quote and will NOT be added to the cart. The button adds only the eligible standard looks.`, { count: quoteOnly })
              : t(studio, 'quote_only_blocked', 'Saved looks need a manual quote (custom length or unavailable cut) and cannot go straight to the cart. Change the cut to standard, or continue shopping.', { count: quoteOnly }))
            : t(studio, 'quote_only_enquiry', 'Saved combinations are ready. Continue shopping to add another, or send all of them on WhatsApp below.', { count: quoteOnly }))
          : (studio.dataset.devCheckout === 'true'
            ? t(studio, 'all_standard', 'All saved looks are standard cuts. Availability is checked again by Shopify when they are added to the cart.')
            : t(studio, 'all_ready_enquiry', 'All saved combinations are ready. Continue shopping to add another, or send them on WhatsApp below.'));
    }
    persistLooks(studio);
  };

  const update = (studio) => {
    const k = studio.querySelector('[data-look-kameez]');
    const summary = studio.querySelector('[data-look-summary]');
    const prices = studio.querySelector('[data-look-prices]');
    const enquiry = studio.querySelector('[data-look-enquire]');
    const error = studio.querySelector('[data-look-error]');
    const lengths = studio.querySelector('[data-look-lengths]');
    const cart = studio.querySelector('[data-look-cart]');
    const save = studio.querySelector('[data-look-save]');
    const kLength = studio.querySelector('[data-look-kameez-length]');
    const wLength = studio.querySelector('[data-look-waistcoat-length]');
    const kCountInput = studio.querySelector('[data-look-kameez-count]');
    const wCountInput = studio.querySelector('[data-look-waistcoat-count]');
    const layersEarly = studio.saraLayers;
    const plainOnly = Boolean(layersEarly && (!layersEarly.waistcoat || !Object.keys(layersEarly.waistcoat).length));
    const pieces = plainOnly ? 'suit' : (studio.querySelector('[data-look-pieces]:checked')?.value || 'both');
    const selectedW = activeWcSelect(studio);
    let w = pieces === 'suit' && !selectedW?.selectedOptions.length
      ? studio.querySelector('[data-look-waistcoat]') : selectedW;
    // Editing a saved look can leave selectedIndex -1 when the stored value's dash
    // or label drifted; recover before we tear down the try-on + second scan.
    ensureSelectHasOption(k);
    if (pieces !== 'suit') ensureSelectHasOption(w);
    if (pieces === 'suit' && (!w || !w.selectedOptions.length)) {
      w = studio.querySelector('[data-look-waistcoat]');
      ensureSelectHasOption(w);
    }
    const custom = studio.querySelector('[data-look-cut]:checked')?.value === 'custom';
    studio.saraCartItems = null;
    studio.saraDraft = null;
    if (cart && !studio.querySelector('[data-look-tray-list]')) cart.hidden = true;
    if (!k || !w || !summary || !prices || !enquiry || !error || !lengths || !kLength || !wLength || !k.selectedOptions.length || !w.selectedOptions.length) {
      if (summary) summary.textContent = '';
      prices?.replaceChildren();
      if (enquiry) enquiry.hidden = true;
      if (save) save.disabled = true;
      if (error) {
        error.hidden = false;
        error.textContent = t(
          studio,
          'shade_restore_failed',
          'Could not restore that colour combination. Pick the fabrics again — both piece photos will return once each shade is selected.',
        );
      }
      // Keep any already-visible selection cards; only hide waistcoat when we
      // truly have no WC option list (do not blank the try-on on a soft miss).
      if (!w?.options?.length) {
        studio.querySelector('[data-look-review-waistcoat]')?.setAttribute('hidden', '');
        studio.querySelector('[data-look-selected-waistcoat-link]')?.setAttribute('hidden', '');
      }
      renderTray(studio);
      return;
    }

    const needK = pieces !== 'waistcoat';
    const needW = pieces !== 'suit';
    const previewK = needK || ((studio.dataset.devCheckout === 'true' || studio.dataset.visualPreview === 'true') && needW);
    const kOption = k.selectedOptions[0];
    const wOption = w.selectedOptions[0];
    const shadeSoldOut = (option) => studio.dataset.devCheckout === 'true'
      && !(option?.dataset.skAvailable === 'true' && Number(option?.dataset.skStock) > 0)
      && !(option?.dataset.wcAvailable === 'true' && Number(option?.dataset.wcStock) > 0);
    const noteSoldOut = (paletteSel, option) => {
      const palette = studio.querySelector(paletteSel);
      if (!palette) return;
      let note = palette.parentElement?.querySelector('[data-look-shade-sold-out]');
      if (!note) {
        note = document.createElement('p');
        note.className = 'sf-look-studio__shade-sold-out';
        note.dataset.lookShadeSoldOut = '';
        note.setAttribute('role', 'status');
        palette.parentElement?.insertBefore(note, palette.nextSibling);
      }
      const out = shadeSoldOut(option);
      note.hidden = !out;
      note.textContent = out ? t(studio, 'shade_sold_out_note', 'Sold out — this shade cannot be added to cart right now.') : '';
    };
    noteSoldOut('[data-look-kameez-palette]', kOption);
    noteSoldOut('[data-look-waistcoat-palette]', wOption);
    k.disabled = !previewK;
    w.disabled = !needW;
    k.closest('.sf-look-studio__field').hidden = !previewK;
    w.closest('.sf-look-studio__field').hidden = !needW;
    const kHeading = studio.querySelector('[data-look-kameez-heading]');
    if (kHeading) kHeading.textContent = needK
      ? t(studio, 'kameez_heading', 'Shalwar kameez fabric')
      : t(studio, 'kameez_heading_preview', 'Preview shalwar kameez colour (not included)');
    const kPalette = studio.querySelector('[data-look-kameez-palette]');
    const wPalette = studio.querySelector('[data-look-waistcoat-palette]');
    if (kPalette) kPalette.hidden = !previewK;
    if (wPalette) wPalette.hidden = !needW;
    const customHint = studio.querySelector('[data-look-custom-hint]');
    if (customHint) customHint.hidden = !custom; /* legacy; cut help now lives in info icon */
    lengths.hidden = !custom;
    kLength.disabled = !custom || !needK;
    wLength.disabled = !custom || !needW;
    for (const input of [kLength, wLength]) input.closest('.sf-look-studio__stepper')?.querySelectorAll?.('button').forEach((button) => { button.disabled = input.disabled; });
    kLength.closest('.sf-look-studio__field').hidden = !needK;
    wLength.closest('.sf-look-studio__field').hidden = !needW;
    const kQtyRow = studio.querySelector('[data-look-qty-row="kameez"]');
    const wQtyRow = studio.querySelector('[data-look-qty-row="waistcoat"]');
    if (kQtyRow) kQtyRow.hidden = !needK;
    else if (kCountInput) kCountInput.closest('.sf-look-studio__field').hidden = !needK;
    if (wQtyRow) wQtyRow.hidden = !needW;
    else if (wCountInput) wCountInput.closest('.sf-look-studio__field').hidden = !needW;
    studio.classList.toggle('sf-look-studio--suit-only', !needW);

    const kColour = kOption.dataset.colour;
    const wColour = wOption.dataset.colour;
    studio.style.setProperty('--sf-look-kameez', previewK ? kColour : '#d3c4af');
    studio.style.setProperty('--sf-look-waistcoat', wColour);
    const kSwatch = studio.querySelector('[data-look-kameez-swatch]');
    const wSwatch = studio.querySelector('[data-look-waistcoat-swatch]');
    const kThumbnail = kOption.dataset.thumb || kOption.dataset.scan;
    const wThumbnail = wOption.dataset.thumb || wOption.dataset.scan;
    if (kSwatch) {
      kSwatch.style.backgroundColor = kColour || '';
      kSwatch.style.backgroundImage = kThumbnail ? `url("${kThumbnail}")` : '';
      kSwatch.hidden = !previewK;
    }
    if (wSwatch) {
      wSwatch.style.backgroundColor = wColour || '';
      wSwatch.style.backgroundImage = wThumbnail ? `url("${wThumbnail}")` : '';
      wSwatch.hidden = !needW;
    }
    /* Fabric proof sits beside each quantity stepper so qty changes map to the cloth. */
    const reviewItems = [
      {
        show: needK,
        option: kOption,
        card: studio.querySelector('[data-look-review-kameez]'),
        image: studio.querySelector('[data-look-review-kameez-img]'),
        name: studio.querySelector('[data-look-review-kameez-name]'),
        button: studio.querySelector('[data-look-review-kameez-btn]'),
        role: t(studio, 'selected_kameez', 'Shalwar kameez'),
        thumb: kThumbnail,
        colour: kColour
      },
      {
        show: needW,
        option: wOption,
        card: studio.querySelector('[data-look-review-waistcoat]'),
        image: studio.querySelector('[data-look-review-waistcoat-img]'),
        name: studio.querySelector('[data-look-review-waistcoat-name]'),
        button: studio.querySelector('[data-look-review-waistcoat-btn]'),
        role: t(studio, 'selected_waistcoat', 'Waistcoat'),
        thumb: wThumbnail,
        colour: wColour
      }
    ];
    for (const item of reviewItems) {
      const shade = item.option.dataset.shade || item.option.value;
      const available = item.show && Boolean(item.thumb || item.colour);
      if (item.card) item.card.hidden = !available;
      if (item.name) item.name.textContent = available ? shade : '';
      if (item.image) {
        if (available && item.thumb) {
          item.image.hidden = false;
          item.image.src = item.thumb;
          item.image.alt = t(studio, 'selected_scan_alt', `${item.option.value} ${item.role} fabric image`, {
            label: item.option.value,
            role: item.role
          });
          item.image.style.backgroundColor = '';
        } else if (available && item.colour) {
          item.image.hidden = false;
          item.image.removeAttribute('src');
          item.image.alt = shade;
          item.image.style.backgroundColor = item.colour;
        } else {
          item.image.hidden = true;
          item.image.removeAttribute('src');
          item.image.alt = '';
          item.image.style.backgroundColor = '';
        }
      }
      if (item.button) {
        item.button.disabled = !available || !item.option.dataset.scan;
        item.button.setAttribute(
          'aria-label',
          t(studio, 'review_enlarge', `Enlarge ${shade} fabric photo`, { shade })
        );
      }
    }
    for (const [select, selector] of [
      [k, '[data-look-kameez-palette]'],
      [studio.querySelector('[data-look-waistcoat]'), '[data-look-waistcoat-palette]'],
      [studio.querySelector('[data-look-waistcoat-design]'), '[data-look-waistcoat-design-palette]']
    ]) {
      const host = studio.querySelector(selector);
      if (!host || !select?.options?.length) continue;
      const nextIndex = String(select.selectedIndex);
      host.querySelectorAll('.sf-look-studio__scan').forEach((button) => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.index) === select.selectedIndex));
      });
      if (host.dataset.sfSelectedIndex !== nextIndex) {
        host.dataset.sfSelectedIndex = nextIndex;
        scrollSelectedSwatch(host);
      } else {
        syncSwatchNav(host);
      }
    }
    const photo = studio.querySelector('[data-look-photo]');
    const scanPreview = studio.querySelector('[data-look-scan-preview]');
    const scanPreviewLink = studio.querySelector('[data-look-scan-preview-link]');
    const photoCaption = studio.querySelector('[data-look-photo-caption]');
    const focusQuality = studio.dataset.focusQuality || '';
    const modelKey = kOption.dataset.modelShade && wOption.dataset.modelShade
      ? `${kOption.dataset.modelShade}-${wOption.dataset.modelShade}` : null;
    const matchedPairModel = focusQuality &&
      kOption.dataset.quality === focusQuality && wOption.dataset.quality === focusQuality &&
      modelKey && studio.saraLookPhotos?.[modelKey];
    const suitOnlyModel = focusQuality &&
      kOption.dataset.quality === focusQuality && kOption.dataset.modelShade &&
      studio.saraSuitPhotos?.[kOption.dataset.modelShade];
    // Try-on layers (2026-10-24): the browser stacks base + shalwar kameez + waistcoat + buttons from a small WebP per
    // shade instead of one pre-combined photo per colour pair. Used when this quality has a layer manifest.
    const layers = studio.saraLayers;
    const layerK = layers && focusQuality && kOption.dataset.quality === focusQuality && kOption.dataset.modelShade;
    const layerW = needW && !isCollectionWc(wOption) && layers && wOption.dataset.quality === focusQuality && wOption.dataset.modelShade;
    const designHandle = isCollectionWc(wOption) ? (wOption.dataset.designHandle || '') : '';
    const designOverlay = designHandle && studio.saraWcDesignLayers?.[designHandle];
    const designTryonOk = Boolean(needW && isCollectionWc(wOption) && (
      isApprovedDesignOverlay(designOverlay) ||
      (isPreviewOnlyWc(wOption) && isReviewPreviewOverlay(designOverlay))
    ));
    const layerLook = layerK && (needW
      ? (designTryonOk && layers.suit?.wc?.[layerK]
        ? { frame: 'wc', k: layerK, w: null, design: designHandle }
        : (layerW && layers.suit?.wc?.[layerK] && layers.waistcoat?.[layerW] ? { frame: 'wc', k: layerK, w: layerW } : null))
      : (layers.suit?.sk?.[layerK] ? { frame: 'sk', k: layerK } : null));
    const collectionFabricOnly = needW && isCollectionWc(wOption) && !designTryonOk && Boolean(wOption.dataset.scan);
    const selectedModel = layerLook ? 'layers' : (needW && !isCollectionWc(wOption) ? matchedPairModel : !needW ? suitOnlyModel : null);
    const scanOnlyPreview = collectionFabricOnly || ((studio.dataset.devCheckout === 'true' || studio.dataset.visualPreview === 'true') && !selectedModel);
    const previewOption = needK ? kOption : wOption;
    const roleKameez = t(studio, 'piece_kameez', 'shalwar kameez');
    const roleWaistcoat = t(studio, 'piece_waistcoat', 'waistcoat');
    const selectedScans = [
      { option: kOption, link: studio.querySelector('[data-look-selected-kameez-link]'), image: studio.querySelector('[data-look-selected-kameez-scan]'), name: studio.querySelector('[data-look-selected-kameez-name]'), role: roleKameez, isKameez: true, show: previewK },
      { option: wOption, link: studio.querySelector('[data-look-selected-waistcoat-link]'), image: studio.querySelector('[data-look-selected-waistcoat-scan]'), name: studio.querySelector('[data-look-selected-waistcoat-name]'), role: roleWaistcoat, isKameez: false, show: needW }
    ];
    for (const item of selectedScans) {
      const src = item.option.dataset.scan;
      const frameSrc = item.option.dataset.frame || src;
      const available = item.show && Boolean(src);
      if (item.link) {
        item.link.hidden = !available;
        if (available) item.link.href = src;
      }
      if (item.image) {
        item.image.hidden = !available;
        if (available) {
          item.image.onerror = () => {
            item.image.onerror = null;
            if (item.image.src !== src) item.image.src = src;
          };
          item.image.src = frameSrc;
          item.image.alt = t(studio, 'selected_scan_alt', `${item.option.value} ${item.role} fabric image`, { label: item.option.value, role: item.role });
        } else item.image.removeAttribute?.('src');
      }
      if (item.name) item.name.textContent = item.show
        ? `${item.option.value}${item.isKameez && !needK ? t(studio, 'styling_ref', ' · styling reference, not included') : ''}`
        : '';
    }
    if (photo) photo.hidden = scanOnlyPreview || Boolean(layerLook);
    renderLayers(studio, photo, layerLook);
    if (scanPreviewLink) {
      scanPreviewLink.hidden = !scanOnlyPreview || !previewOption.dataset.scan;
      if (scanOnlyPreview && previewOption.dataset.scan) scanPreviewLink.href = previewOption.dataset.scan;
    }
    if (scanPreview) {
      scanPreview.hidden = !scanOnlyPreview || !previewOption.dataset.scan;
      if (scanOnlyPreview && previewOption.dataset.scan) {
        scanPreview.onerror = () => {
          scanPreview.onerror = null;
          scanPreview.src = previewOption.dataset.scan;
        };
        scanPreview.src = previewOption.dataset.frame || previewOption.dataset.scan;
        scanPreview.alt = t(studio, 'scan_preview_alt', `${previewOption.value} ${needK ? 'shalwar kameez' : 'waistcoat'} fabric image`, {
          label: previewOption.value,
          piece: needK ? roleKameez : roleWaistcoat
        });
      }
    }
    if (collectionFabricOnly && photoCaption) {
      photoCaption.textContent = t(
        studio,
        'wc_tryon_unavailable',
        'A model preview for this design on the current pose is not approved yet. Compare the fabric image.'
      );
    } else if (scanOnlyPreview && photoCaption) {
      photoCaption.textContent = !needW
        ? (kOption.dataset.scan
          ? t(studio, 'photo_suit_only_scan', `Shalwar kameez only: ${kOption.value}. This image shows the actual fabric; no waistcoat is included. A verified waistcoat-free model image is not available yet.`, { name: kOption.value })
          : t(studio, 'photo_suit_only_no_scan', `Shalwar kameez only: ${kOption.value}. A verified waistcoat-free model image and fabric image are not available yet; no waistcoat is included.`, { name: kOption.value }))
        : t(studio, 'photo_scan_only', `${needK ? 'Shalwar kameez' : 'Waistcoat'} fabric: ${previewOption.value}.`, {
            piece: needK ? roleKameez : roleWaistcoat,
            name: previewOption.value,
            extra: needK && needW
              ? t(studio, 'photo_scan_extra_both', `Waistcoat choice: ${wOption.value}; compare both fabric images.`, { waistcoat: wOption.value })
              : t(studio, 'photo_scan_extra_wc', 'Only the waistcoat fabric is included.')
          });
    } else if (photo && photoCaption && selectedModel) {
      if (!layerLook) photo.src = selectedModel;
      photo.alt = needW
        ? t(studio, 'photo_model_both_alt', `Model visualisation of ${kOption.value} shalwar kameez and ${wOption.value} waistcoat`, { kameez: kOption.value, waistcoat: wOption.value })
        : t(studio, 'photo_model_suit_alt', `Model visualisation wearing ${kOption.value} shalwar kameez without a waistcoat`, { kameez: kOption.value });
      photoCaption.textContent = !needW
        ? t(studio, 'photo_caption_suit', `Shalwar kameez-only visualisation for ${kOption.value}; no waistcoat is shown. The selected shalwar kameez fabric is included. Match is tied to this exact shade ID; compare the fabric image for the cloth itself. Unstitched fabric is supplied.`, { name: kOption.value })
        : needK
          ? t(studio, 'photo_caption_three', `Three-piece visualisation for ${kOption.value} shalwar kameez colour and ${wOption.value} waistcoat. Both fabrics are selected. Model colours are appearance-matched; physical lot identity and exact screen colour are not certified. Unstitched fabric is supplied.`, { kameez: kOption.value, waistcoat: wOption.value })
          : t(studio, 'photo_caption_wc_only', `Waistcoat-only styling visualisation for ${kOption.value} shalwar kameez colour and ${wOption.value} waistcoat. Only waistcoat fabric is included; the shalwar kameez colour is a preview reference. Model colours are appearance-matched; physical lot identity and exact screen colour are not certified. Unstitched fabric is supplied.`, { kameez: kOption.value, waistcoat: wOption.value });
    }
    if (photoCaption && studio.dataset.visualPreview === 'true' && selectedModel) {
      photoCaption.textContent = t(studio, 'visual_caption', 'Approximate colour visualisation. Compare the fabric images below.');
    } else if (photoCaption && String(selectedModel || '').includes('sara-medora-ai-preview-')) {
      photoCaption.textContent += t(studio, 'ai_preview_note', ' AI-assisted approximate color visualization for this unpublished preview; the enlarged fabric images beside the model show your selections.');
    }
    if (photoCaption && isPreviewOnlyWc(wOption) && designTryonOk) {
      photoCaption.textContent = t(studio, 'wc_preview_model_caption', 'Digital styling preview. Compare the actual fabric image. Ordering opens after stock is confirmed.');
    }

    // Preview-only fabric images have no verified Shopify stock or sale data.
    // Keep the wheel interactive without allowing a cart line.
    if (isPreviewOnlyWc(wOption) && needW) {
      prices.replaceChildren();
      summary.textContent = t(studio, 'wc_preview_summary', 'Coming soon · waistcoat design Rs 1,000/m; standard 1.25 m cut Rs 1,250.');
      error.hidden = true;
      error.textContent = '';
      if (save) save.disabled = true;
      if (cart) cart.hidden = true;
      enquiry.hidden = true;
      studio.saraDraft = null;
      renderTray(studio);
      return;
    }

    const K_STD_M = 4.25;
    const W_STD_M = 1.25;
    const kMetres = custom ? Number(kLength.value) : K_STD_M;
    const wMetres = custom ? Number(wLength.value) : W_STD_M;
    const kCount = needK ? (kCountInput ? Number(kCountInput.value) : 1) : 0;
    const wCount = needW ? (wCountInput ? Number(wCountInput.value) : 1) : 0;
    const validCount = (input, count) => !input || (Number.isInteger(count) && count >= 0 && count <= Number(input.max));
    const activeK = needK && kCount > 0;
    const activeW = needW && wCount > 0;
    // Suit cut anchors the quality rate; matching waistcoat inherits it.
    // Collection designs use Rs 1,000/m (Rs 1,250 at the 1.25 m standard cut).
    const kRate = qualityRate(kOption);
    const wRate = isCollectionWc(wOption) ? (Number(wOption.dataset.rate) || 1000) : qualityRate(wOption);
    const kCustomIncrement = Number(kOption.dataset.customRounding) || 50;
    const wCustomIncrement = Number(wOption.dataset.customRounding) || 50;
    const validLength = (input, metres) => {
      if (!/^\d+(?:\.\d{1,2})?$/.test(input.value) || !Number.isFinite(metres) || metres < 0.25 || metres > 100) return false;
      return Math.round(metres * 100) % 25 === 0;
    };
    const ratesOk = (!activeK || (Number.isFinite(kRate) && kRate > 0)) &&
      (!activeW || (Number.isFinite(wRate) && wRate > 0));
    const valid = (activeK || activeW) && validCount(kCountInput, kCount) && validCount(wCountInput, wCount) &&
      (!activeK || (!custom || validLength(kLength, kMetres))) && (!activeW || (!custom || validLength(wLength, wMetres))) &&
      ratesOk;
    error.hidden = valid;
    error.textContent = valid ? '' : t(studio, 'validation_qty', 'Choose at least one piece and enter a whole-number quantity between 0 and 20. Custom metres must be between 0.25 m and 100 m, in 0.25 m steps.');
    /* WhatsApp visibility is set after cart eligibility (below). */
    if (save) save.disabled = !valid;
    prices.replaceChildren();
    if (!valid) {
      summary.textContent = t(studio, 'validation_custom', 'Please check your custom lengths.');
      renderTray(studio);
      return;
    }

    const kStandard = isStandardCut(kMetres, K_STD_M);
    const wStandard = isStandardCut(wMetres, W_STD_M);
    const kUnitPrice = priceForQualityCut(kOption, kMetres, { role: 'kameez' });
    const wUnitPrice = priceForQualityCut(wOption, wMetres, { role: 'waistcoat' });
    const kPrice = activeK ? kUnitPrice * kCount : 0;
    const wPrice = activeW ? wUnitPrice * wCount : 0;
    const parts = [];
    if (activeK) parts.push(t(studio, 'summary_kameez', `${kCount} × ${kOption.value} shalwar kameez (${formatMetres(kMetres)} m each)`, { count: kCount, name: kOption.value, metres: formatMetres(kMetres) }));
    if (activeW) parts.push(t(studio, 'summary_waistcoat', `${wCount} × ${wOption.value} waistcoat (${formatMetres(wMetres)} m each)`, { count: wCount, name: wOption.value, metres: formatMetres(wMetres) }));
    summary.textContent = parts.join(' + ');
    const priceTable = document.createElement('table');
    priceTable.className = 'sf-look-studio__price-table';
    const priceBody = document.createElement('tbody');
    const addPrice = (label, count, metres, rate, increment, unitPrice, standard) => {
      const row = document.createElement('tr');
      const item = document.createElement('th');
      item.scope = 'row';
      item.textContent = label;
      const detail = document.createElement('td');
      detail.className = 'sf-look-studio__price-detail';
      detail.textContent = standard
        ? t(studio, 'price_standard_detail', `${count} × standard ${formatMetres(metres)} m cut`, { count, metres: formatMetres(metres) })
        : t(studio, 'price_custom_detail', `${count} × ${formatMetres(metres)} m × ${money(rate)}/m`, { count, metres: formatMetres(metres), rate: money(rate) });
      const amount = document.createElement('td');
      amount.className = 'sf-look-studio__price-amount';
      amount.textContent = money(unitPrice * count);
      row.append(item, detail, amount);
      priceBody.appendChild(row);
    };
    if (activeK) addPrice(t(studio, 'price_kameez', 'Shalwar kameez fabric'), kCount, kMetres, kRate, kCustomIncrement, kUnitPrice, kStandard);
    if (activeW) addPrice(
      isCollectionWc(wOption)
        ? t(studio, 'price_waistcoat_design', 'Waistcoat design fabric')
        : t(studio, 'price_waistcoat', 'Waistcoat fabric'),
      wCount, wMetres, wRate, wCustomIncrement, wUnitPrice, wStandard
    );
    priceTable.appendChild(priceBody);
    const priceFoot = document.createElement('tfoot');
    const totalRow = document.createElement('tr');
    const totalLabel = document.createElement('th');
    totalLabel.scope = 'row';
    totalLabel.colSpan = 2;
    totalLabel.textContent = activeK && activeW
      ? t(studio, 'price_three_piece', 'Three-piece fabric total')
      : t(studio, 'price_estimated', 'Estimated fabric total');
    const totalAmount = document.createElement('td');
    totalAmount.className = 'sf-look-studio__price-amount';
    totalAmount.textContent = money(kPrice + wPrice);
    totalRow.append(totalLabel, totalAmount);
    priceFoot.appendChild(totalRow);
    priceTable.appendChild(priceFoot);
    prices.appendChild(priceTable);
    const cutLabel = custom ? t(studio, 'wa_cut_custom', 'custom length') : t(studio, 'wa_cut_standard', 'standard length');
    const lines = [
      t(studio, 'wa_intro_one', 'Hello Sara Fabrics, please check whether the selected unstitched fabric is currently available before I place an order:'),
      '',
      t(studio, 'wa_heading', 'SELECTED FABRIC ORDER ENQUIRY'),
      activeK ? t(studio, 'wa_line_kameez', `1. Shalwar kameez fabric — ${kOption.value}
   Quantity: ${kCount} × ${formatMetres(kMetres)} m each
   Selected-fabric amount: ${money(kPrice)}`, { n: 1, name: kOption.value, count: kCount, metres: formatMetres(kMetres), amount: money(kPrice) }) : null,
      activeW ? t(studio, 'wa_line_waistcoat', `${activeK ? '2' : '1'}. Waistcoat fabric — ${wOption.value}
   Quantity: ${wCount} × ${formatMetres(wMetres)} m each
   Selected-fabric amount: ${money(wPrice)}`, { n: activeK ? 2 : 1, name: wOption.value, count: wCount, metres: formatMetres(wMetres), amount: money(wPrice) }) : null,
      !activeK && activeW ? t(studio, 'wa_styling_ref', `Styling reference only (not included): ${kOption.value} shalwar kameez colour`, { name: kOption.value }) : null,
      t(studio, 'wa_estimated', `Estimated fabric total: ${money(kPrice + wPrice)}`, { amount: money(kPrice + wPrice) }),
      t(studio, 'wa_outro', `Cut requested: ${cutLabel}. Please confirm availability for each selected quality and shade, then confirm exact shade, cutting, final price and delivery details. This message is an enquiry, not a confirmed order.`, { cut: cutLabel })
    ].filter(Boolean);
    enquiry.href = quoteUrl(enquiry.dataset.baseHref, lines);

    const draft = {
      pieces, custom, kValue: k.value, wValue: w.value,
      wcSource: isCollectionWc(wOption) ? 'collection' : 'matching',
      designHandle: wOption.dataset.designHandle || '',
      kCount, wCount, kMetres, wMetres,
      summary: parts.join(' + '), total: kPrice + wPrice,
      photo: resolveLookPhoto(studio, { layerLook, scanOnlyPreview, photo, scanPreview }),
      photoIsScan: scanOnlyPreview && !layerLook, cartItems: null
    };
    studio.saraDraft = draft;

    const checkoutHosts = new Set([
      'sara-fabrics-winter-2026-checkout-lab.myshopify.com',
      'shop.sarafabrics.com',
      'sarafabrics.myshopify.com'
    ]);
    if (cart && studio.dataset.devCheckout === 'true' && checkoutHosts.has(location.hostname) && !custom) {
      const cutReasons = [];
      const cutItem = (option, piece, metres, count, unitPrice, variantKey, priceKey, availableKey) => {
        const id = Number(option.dataset[variantKey]);
        const variantPrice = Number(option.dataset[priceKey]);
        const stockKey = availableKey === 'skAvailable' ? 'skStock' : 'wcStock';
        const stock = Number(option.dataset[stockKey]);
        const cm = Math.round(metres * 100);
        const poolCm = Number(option.dataset.poolCm);
        const poolKey = option.dataset.poolKey || `${option.dataset.quality}|${option.dataset.shadeId || option.dataset.shade}`;
        if (piece === 'waistcoat' && isCollectionWc(option) && option.dataset.priceOk !== 'true') {
          cutReasons.push(t(studio, 'reason_design_price', 'waistcoat design: Shopify price must be Rs 1,250 for the 1.25 m cut'));
          return null;
        }
        if (!Number.isSafeInteger(id) || id <= 0) {
          cutReasons.push(t(studio, 'reason_missing_variant', `${piece}: missing Shopify variant`, { piece }));
          return null;
        }
        if (variantPrice !== unitPrice * 100) {
          cutReasons.push(t(studio, 'reason_price_mismatch', `${piece}: listed price does not match the Shopify variant`, { piece }));
          return null;
        }
        const shortStock = !Number.isInteger(stock) || stock < count;
        const markedUnavailable = option.dataset[availableKey] !== 'true';
        const preorder = markedUnavailable || shortStock;
        const properties = {
          Fabric: option.dataset.quality,
          Shade: option.dataset.shade,
          Cut: `${formatMetres(metres)} m`,
          _piece: piece,
          ...(preorder ? { Fulfilment: 'Pre-order — upcoming stock' } : {}),
          ...(!activeK && piece === 'waistcoat' ? { 'Shalwar kameez styling reference (not included)': kOption.value } : {})
        };
        if (piece === 'waistcoat' && isCollectionWc(option)) {
          properties['Waistcoat source'] = 'Design collection';
          properties.Design = option.dataset.designHandle || option.value;
          properties['Source quality'] = option.dataset.quality || '';
          delete properties.poolKey;
        }
        return {
          id,
          quantity: count,
          stock: Number.isInteger(stock) ? stock : 0,
          cm: isCollectionWc(option) ? undefined : cm,
          poolCm: isCollectionWc(option) ? undefined : (Number.isFinite(poolCm) && poolCm > 0 ? poolCm : undefined),
          poolKey: isCollectionWc(option) ? undefined : poolKey,
          properties
        };
      };
      /* Same-shade SK + matching WC share one metre pool — short pool becomes pre-order, not a hard block. */
      let poolPreorder = false;
      if (
        activeK && activeW && !isCollectionWc(wOption) &&
        kOption.dataset.poolKey && kOption.dataset.poolKey === wOption.dataset.poolKey
      ) {
        const poolCm = Number(kOption.dataset.poolCm);
        const needCm = (kCount * 425) + (wCount * 125);
        if (Number.isFinite(poolCm) && poolCm > 0 && needCm > poolCm) {
          poolPreorder = true;
        }
      }
      const items = [
        activeK ? cutItem(kOption, 'shalwar kameez', 4.25, kCount, kUnitPrice, 'skVariantId', 'skPriceCents', 'skAvailable') : null,
        activeW ? cutItem(wOption, 'waistcoat', 1.25, wCount, wUnitPrice, 'wcVariantId', 'wcPriceCents', 'wcAvailable') : null
      ].filter(Boolean);
      if (poolPreorder) {
        items.forEach((item) => {
          if (!item.properties) item.properties = {};
          item.properties.Fulfilment = 'Pre-order — upcoming stock';
        });
      }
      if (items.length === Number(activeK) + Number(activeW) && !cutReasons.length) {
        draft.cartItems = items;
        draft.preorder = poolPreorder || items.some((item) => item.properties?.Fulfilment === 'Pre-order — upcoming stock');
        if (!studio.querySelector('[data-look-tray-list]')) {
          studio.saraCartItems = items;
          cart.hidden = false;
        }
      } else if (cutReasons.length && !custom) {
        draft.cartBlockReason = cutReasons.join(' · ');
      }
    }
    /* Checkout pages: cart is the only primary CTA. WhatsApp stays on the sticky
       float / help dialog — never as a Look Studio enquire button. */
    if (enquiry) {
      const checkoutPage = studio.dataset.devCheckout === 'true';
      enquiry.hidden = checkoutPage ? true : !valid;
      enquiry.classList.remove('sf-look-studio__enquire--secondary');
    }
    renderTray(studio);
  };

  const FRAME_SCALE_KEY = 'sf-look-frame-scale';
  /* Compact portrait: 100% default, up to 400%. Landscape: 75% default, up to 100% (portrait default). */
  const FRAME_SCALE_COMPACT_KEY = 'sf-look-frame-scale-compact-v2';
  const FRAME_SCALE_COMPACT_LANDSCAPE_KEY = 'sf-look-frame-scale-compact-landscape-v2';
  const isCompactFrameViewport = () => window.matchMedia('(max-width: 1199px)').matches;
  const isLandscapeFrameViewport = () => window.matchMedia('(orientation: landscape)').matches;
  const frameScaleStorageKey = () => {
    if (!isCompactFrameViewport()) return FRAME_SCALE_KEY;
    return isLandscapeFrameViewport() ? FRAME_SCALE_COMPACT_LANDSCAPE_KEY : FRAME_SCALE_COMPACT_KEY;
  };
  const defaultFramePct = () => {
    if (!isCompactFrameViewport()) return 100;
    return isLandscapeFrameViewport() ? 75 : 100;
  };
  const minFramePct = () => 35;
  const maxFramePct = () => {
    if (!isCompactFrameViewport()) return 100;
    /* Landscape: enlarge only up to the portrait default size. */
    return isLandscapeFrameViewport() ? 100 : 400;
  };
  const clampFramePct = (value, fallback = 100) => Math.min(maxFramePct(), Math.max(minFramePct(), Math.round(Number(value) || fallback)));
  const readSavedFramePct = () => {
    const fallback = defaultFramePct();
    try {
      const raw = sessionStorage.getItem(frameScaleStorageKey());
      return raw == null || raw === '' ? fallback : clampFramePct(raw, fallback);
    } catch (err) {
      return fallback;
    }
  };
  const applyFrameScale = (studio, pct) => {
    const next = clampFramePct(pct, defaultFramePct());
    const scale = String(next / 100);
    studio.saraFramePct = next;
    /* model-panel uses display:contents on phones — set the var on figure too */
    [studio.querySelector('.sf-look-studio__model-panel'), studio.querySelector('[data-look-frame]'), studio.querySelector('.sf-look-studio__figure'), studio]
      .filter(Boolean)
      .forEach((node) => node.style.setProperty('--sf-look-frame-scale', scale));
    studio.querySelectorAll('[data-look-frame-handle]').forEach((handle) => {
      handle.setAttribute('aria-valuemin', String(minFramePct()));
      handle.setAttribute('aria-valuemax', String(maxFramePct()));
      handle.setAttribute('aria-valuenow', String(next));
      handle.setAttribute('aria-valuetext', `${next}%`);
    });
    try { sessionStorage.setItem(frameScaleStorageKey(), String(next)); } catch (err) { /* ignore */ }
  };
  const bindFrameScale = (studio) => {
    const figure = studio.querySelector('[data-look-frame]');
    const handles = studio.querySelectorAll('[data-look-frame-handle]');
    if (!figure || !handles.length || figure.dataset.lookFrameBound) return;
    figure.dataset.lookFrameBound = 'true';
    applyFrameScale(studio, readSavedFramePct());
    const syncOrientationScale = () => applyFrameScale(studio, readSavedFramePct());
    window.matchMedia('(orientation: landscape)').addEventListener?.('change', syncOrientationScale);
    window.matchMedia('(max-width: 1199px)').addEventListener?.('change', syncOrientationScale);
    handles.forEach((handle) => {
      handle.setAttribute('aria-valuemin', String(minFramePct()));
      handle.setAttribute('aria-valuemax', String(maxFramePct()));
      handle.addEventListener('pointerdown', (event) => {
        if (event.button != null && event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        const corner = handle.dataset.lookFrameHandle || 'br';
        const startPct = studio.saraFramePct || defaultFramePct();
        const startW = Math.max(figure.getBoundingClientRect().width, 1);
        /* Drag against the 100% (portrait-default) width so pct mapping stays even. */
        const baseW = Math.max(startW / (startPct / 100), 1);
        const startX = event.clientX;
        const startY = event.clientY;
        figure.classList.add('is-resizing');
        handle.setPointerCapture?.(event.pointerId);
        const onMove = (moveEvent) => {
          const dx = moveEvent.clientX - startX;
          const dy = moveEvent.clientY - startY;
          const delta = corner === 'bl' ? ((-dx) + dy) / 2 : (dx + dy) / 2;
          applyFrameScale(studio, startPct + (delta / baseW) * 100);
        };
        const onUp = (upEvent) => {
          figure.classList.remove('is-resizing');
          try { handle.releasePointerCapture?.(upEvent.pointerId); } catch (err) { /* ignore */ }
          handle.removeEventListener('pointermove', onMove);
          handle.removeEventListener('pointerup', onUp);
          handle.removeEventListener('pointercancel', onUp);
        };
        handle.addEventListener('pointermove', onMove);
        handle.addEventListener('pointerup', onUp);
        handle.addEventListener('pointercancel', onUp);
      });
      handle.addEventListener('keydown', (event) => {
        const step = event.shiftKey ? 25 : 10;
        let next = studio.saraFramePct || defaultFramePct();
        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next += step;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next -= step;
        else return;
        event.preventDefault();
        applyFrameScale(studio, next);
      });
    });
  };

  const waistcoatPartForLook = (studio, look) => {
    if (!look) return null;
    if (look.design) return studio.saraWcDesignLayers?.[look.design] || null;
    return look.w ? studio.saraLayers?.waistcoat?.[look.w] : null;
  };

  const layerPartsForLook = (studio, look) => {
    const L = studio.saraLayers;
    if (!L?.size || !look) return [];
    return [
      L.frames[look.frame],
      L.suit[look.frame]?.[look.k],
      waistcoatPartForLook(studio, look),
      L.frames[`${look.frame}_buttons`]
    ].filter(Boolean);
  };

  const layerPhotoKey = (look) => (look ? `${look.frame}|${look.k}|${look.w || ''}|${look.design || ''}` : '');

  /* Rebuild try-on layer ids from a saved look so tray thumbs can be backfilled
     (first save often races compose; localStorage also strips data-URL photos). */
  const layerLookFromSaved = (studio, saved) => {
    const L = studio.saraLayers;
    const focusQuality = studio.dataset.focusQuality || '';
    if (!L || !focusQuality || !saved) return null;
    const kOpt = findShadeOption(studio, saved.kValue);
    if (!kOpt || kOpt.dataset.quality !== focusQuality || !kOpt.dataset.modelShade) return null;
    const layerK = kOpt.dataset.modelShade;
    const needW = saved.pieces !== 'suit' && (Number(saved.wCount) || 0) > 0;
    if (!needW) return L.suit?.sk?.[layerK] ? { frame: 'sk', k: layerK } : null;
    const collection = saved.wcSource === 'collection';
    const wOpt = findShadeOption(studio, saved.wValue, { collection });
    if (!wOpt) return null;
    if (collection) {
      const designHandle = wOpt.dataset.designHandle || '';
      const designOverlay = designHandle && studio.saraWcDesignLayers?.[designHandle];
      const designTryonOk = Boolean(
        isApprovedDesignOverlay(designOverlay) ||
        (isPreviewOnlyWc(wOpt) && isReviewPreviewOverlay(designOverlay))
      );
      if (designTryonOk && L.suit?.wc?.[layerK]) {
        return { frame: 'wc', k: layerK, w: null, design: designHandle };
      }
      return null;
    }
    if (wOpt.dataset.quality !== focusQuality || !wOpt.dataset.modelShade) return null;
    const layerW = wOpt.dataset.modelShade;
    if (L.suit?.wc?.[layerK] && L.waistcoat?.[layerW]) {
      return { frame: 'wc', k: layerK, w: layerW };
    }
    return null;
  };

  const scanFallbackPhoto = (studio, saved) => {
    const kOpt = findShadeOption(studio, saved.kValue);
    const wOpt = saved.wcSource === 'collection'
      ? findShadeOption(studio, saved.wValue, { collection: true })
      : findShadeOption(studio, saved.wValue);
    return kOpt?.dataset.thumb || kOpt?.dataset.scan || wOpt?.dataset.thumb || wOpt?.dataset.scan || null;
  };

  const loadCorsImage = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`layer image failed: ${src}`));
    img.src = src;
  });

  /* Warm the browser cache so the first sk↔wc frame switch does not paint a half-loaded stack. */
  const preloadLayerManifest = (studio, L) => {
    if (!L || studio.saraLayersPreloading) return;
    studio.saraLayersPreloading = true;
    const urls = new Set();
    const take = (part) => { if (part?.src) urls.add(part.src); };
    Object.values(L.frames || {}).forEach(take);
    Object.values(L.suit?.sk || {}).forEach(take);
    Object.values(L.suit?.wc || {}).forEach(take);
    Object.values(L.waistcoat || {}).forEach(take);
    afterLoadIdle(() => urls.forEach((src) => {
      const img = new Image();
      img.decoding = 'async';
      img.crossOrigin = 'anonymous';
      img.src = src;
    }));
  };

  const whenLayerSrcReady = (src) => new Promise((resolve) => {
    if (!src) { resolve(); return; }
    const img = new Image();
    setImageCors(img, src);
    img.decoding = 'async';
    const done = () => resolve();
    img.onload = done;
    img.onerror = done;
    img.src = src;
    if (img.complete) done();
  });

  const composeLayerPhoto = async (studio, look) => {
    const L = studio.saraLayers;
    const parts = layerPartsForLook(studio, look);
    if (!L?.size || !parts.length) return null;
    const [fw, fh] = L.size;
    const scale = Math.min(1, 640 / fw);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(fw * scale));
    canvas.height = Math.max(1, Math.round(fh * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return parts[0]?.src || null;
    try {
      const images = await Promise.all(parts.map((part) => loadCorsImage(part.src)));
      images.forEach((img, i) => {
        const part = parts[i];
        const width = (part.w / fw) * canvas.width;
        const height = width * (img.naturalHeight / img.naturalWidth);
        const left = (part.x / fw) * canvas.width;
        const top = (part.y / fh) * canvas.height;
        ctx.drawImage(img, left, top, width, height);
      });
      return canvas.toDataURL('image/jpeg', 0.84);
    } catch (err) {
      return null;
    }
  };

  const cacheLayerPhoto = (studio, look) => {
    const key = layerPhotoKey(look);
    studio.saraLayerPhotoKey = key;
    if (!look) {
      studio.saraLayerPhoto = null;
      return;
    }
    const token = (studio.saraLayerPhotoToken = (studio.saraLayerPhotoToken || 0) + 1);
    /* Saving a look composes on demand if this has not run yet. */
    new Promise((resolve) => afterLoadIdle(resolve)).then(() => composeLayerPhoto(studio, look)).then((dataUrl) => {
      if (studio.saraLayerPhotoToken !== token || studio.saraLayerPhotoKey !== key) return;
      studio.saraLayerPhoto = dataUrl;
      if (studio.saraDraft && studio.saraLayerLook) studio.saraDraft.photo = dataUrl || studio.saraDraft.photo;
      /* Patch already-saved looks that raced ahead of this compose. */
      let patched = false;
      (studio.saraSavedLooks || []).forEach((saved) => {
        if (saved.photo || !dataUrl) return;
        const savedLayer = layerLookFromSaved(studio, saved);
        if (savedLayer && layerPhotoKey(savedLayer) === key) {
          saved.photo = dataUrl;
          saved.photoIsScan = false;
          patched = true;
        }
      });
      if (patched) renderTray(studio);
    });
  };

  const applyLayerParts = (studio, stack, look, parts) => {
    const L = studio.saraLayers;
    const imgs = [...stack.querySelectorAll('img')];
    parts.forEach((part, i) => {
      let img = imgs[i];
      if (!img) {
        img = document.createElement('img');
        img.alt = '';
        img.decoding = 'async';
        stack.appendChild(img);
      }
      if (!part) { img.hidden = true; return; }
      img.hidden = false;
      setImageCors(img, part.src);
      img.style.left = `${100 * part.x / L.size[0]}%`;
      img.style.top = `${100 * part.y / L.size[1]}%`;
      img.style.width = `${100 * part.w / L.size[0]}%`;
      if (img.getAttribute('src') !== part.src) img.src = part.src;
    });
    for (let i = parts.length; i < imgs.length; i += 1) imgs[i].hidden = true;
    studio.saraLayerLook = look;
    cacheLayerPhoto(studio, look);
  };

  const renderLayers = (studio, photo, look) => {
    let stack = studio.querySelector('[data-look-layer-stack]');
    const nextKey = layerPhotoKey(look);
    if (!look) {
      studio.saraLayerRenderToken = (studio.saraLayerRenderToken || 0) + 1;
      studio.saraLayerLook = null;
      studio.saraLayerPhoto = null;
      studio.saraLayerPhotoKey = '';
      if (stack) stack.hidden = true;
      return;
    }
    const L = studio.saraLayers;
    if (!stack) {
      stack = document.createElement('div');
      stack.className = 'sf-look-studio__layers';
      stack.dataset.lookLayerStack = '';
      stack.setAttribute('role', 'img');
      stack.style.aspectRatio = `${L.size[0]} / ${L.size[1]}`;
      (photo?.parentNode || studio).insertBefore(stack, photo || null);
    }
    stack.hidden = false;
    stack.setAttribute('aria-label', photo?.alt || '');
    const priorKey = layerPhotoKey(studio.saraLayerLook);
    if (priorKey === nextKey) return;
    const parts = [
      L.frames[look.frame],
      L.suit[look.frame]?.[look.k],
      waistcoatPartForLook(studio, look),
      L.frames[`${look.frame}_buttons`]
    ];
    const needed = parts.filter(Boolean).map((part) => part.src);
    const token = (studio.saraLayerRenderToken = (studio.saraLayerRenderToken || 0) + 1);
    const commit = () => {
      if (studio.saraLayerRenderToken !== token) return;
      if (layerPhotoKey(studio.saraLayerLook) === nextKey) return;
      applyLayerParts(studio, stack, look, parts);
    };
    /* First paint can stream in. Later switches (esp. wc→sk) keep the prior complete
       stack until every new part is decoded — otherwise the waistcoat hides while the
       sk frame/suit are still loading and the first switch ghosts/misaligns. */
    if (!priorKey || !needed.length) { commit(); return; }
    Promise.all(needed.map(whenLayerSrcReady)).then(commit);
  };

  /* Snapshot the on-page try-on stack so saved looks match the product-page model. */
  const captureLayerStackPhoto = (studio) => {
    const look = studio.saraLayerLook;
    if (!look) return null;
    if (studio.saraLayerPhoto && studio.saraLayerPhotoKey === layerPhotoKey(look)) return studio.saraLayerPhoto;
    const stack = studio.querySelector('[data-look-layer-stack]');
    const L = studio.saraLayers;
    if (!stack || stack.hidden || !L?.size) return null;
    const parts = layerPartsForLook(studio, look);
    const stackImgs = [...stack.querySelectorAll('img')].filter((img) => !img.hidden && img.complete && img.naturalWidth > 0);
    const ready = parts.map((part) => {
      const img = stackImgs.find((candidate) => {
        const src = candidate.getAttribute('src') || '';
        return src === part.src || candidate.src.endsWith(part.src.replace(/^https?:/, '')) || candidate.src.includes(part.src.split('/').pop());
      });
      return img ? { ...part, img } : null;
    }).filter(Boolean);
    if (!ready.length) return parts[0]?.src || null;

    const [fw, fh] = L.size;
    const scale = Math.min(1, 640 / fw);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(fw * scale));
    canvas.height = Math.max(1, Math.round(fh * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return ready[0].img.src;
    try {
      ready.forEach((part) => {
        const width = (part.w / fw) * canvas.width;
        const height = width * (part.img.naturalHeight / part.img.naturalWidth);
        const left = (part.x / fw) * canvas.width;
        const top = (part.y / fh) * canvas.height;
        ctx.drawImage(part.img, left, top, width, height);
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.84);
      studio.saraLayerPhoto = dataUrl;
      studio.saraLayerPhotoKey = layerPhotoKey(look);
      return dataUrl;
    } catch (err) {
      /* Never fall back to the hidden legacy studio photo.src. */
      return parts[0]?.src || null;
    }
  };

  const resolveLookPhoto = (studio, { layerLook, scanOnlyPreview, photo, scanPreview }) => {
    if (layerLook) return captureLayerStackPhoto(studio);
    if (scanOnlyPreview) return (scanPreview && !scanPreview.hidden ? scanPreview.src : null);
    return photo?.src || null;
  };

  const init = () => {
    document.querySelectorAll('[data-sara-look-studio]').forEach((studio) => {
      if (studio.dataset.lookReady) return;
      studio.dataset.lookReady = 'true';
      const photoManifest = studio.querySelector('[data-look-photos]');
      try {
        if (photoManifest) studio.saraLookPhotos = JSON.parse(photoManifest.textContent);
      } catch (err) {
        console.error('Sara Look Studio: look photo manifest JSON failed', err);
        studio.saraLookPhotos = {};
      }
      const suitPhotoManifest = studio.querySelector('[data-look-suit-photos]');
      try {
        if (suitPhotoManifest) studio.saraSuitPhotos = JSON.parse(suitPhotoManifest.textContent);
      } catch (err) {
        console.error('Sara Look Studio: suit photo manifest JSON failed', err);
        studio.saraSuitPhotos = {};
      }
      const layerManifest = studio.querySelector('[data-look-layers]');
      try {
        if (layerManifest) studio.saraLayers = JSON.parse(layerManifest.textContent);
        if (studio.saraLayers && !studio.saraLayers.size) studio.saraLayers = null;
        if (studio.saraLayers) preloadLayerManifest(studio, studio.saraLayers);
      } catch (err) {
        console.error('Sara Look Studio: try-on layer manifest JSON failed', err);
        studio.saraLayers = null;
      }
      const designLayerManifest = studio.querySelector('[data-look-wc-design-layers]');
      try {
        studio.saraWcDesignLayers = designLayerManifest
          ? JSON.parse(designLayerManifest.textContent || '{}')
          : {};
      } catch (err) {
        console.error('Sara Look Studio: waistcoat design layer manifest JSON failed', err);
        studio.saraWcDesignLayers = {};
      }
      studio.saraSavedLooks = [];
      setWcSource(studio, 'matching');
      studio.querySelectorAll('[data-look-wc-source]').forEach((btn) => {
        btn.addEventListener('click', () => {
          setWcSource(studio, btn.dataset.lookWcSource);
          update(studio);
        });
      });
      studio.querySelectorAll('[data-look-wc-source-tabs]').forEach((tabs) => {
        tabs.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          const buttons = [...tabs.querySelectorAll('[data-look-wc-source]')];
          const current = buttons.indexOf(document.activeElement);
          if (current < 0) return;
          event.preventDefault();
          const target = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
            : (current + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
          buttons[target].focus();
          setWcSource(studio, buttons[target].dataset.lookWcSource);
          update(studio);
        });
      });
      studio.addEventListener('toggle', (event) => {
        const opened = event.target;
        if (!(opened instanceof HTMLDetailsElement) || !opened.classList.contains('sf-look-studio__info') || !opened.open) return;
        studio.querySelectorAll('details.sf-look-studio__info[open]').forEach((other) => {
          if (other !== opened) other.open = false;
        });
      }, true);
      bindFrameScale(studio);
      renderPalette(studio, studio.querySelector('[data-look-kameez]'), '[data-look-kameez-palette]');
      renderPalette(studio, studio.querySelector('[data-look-waistcoat]'), '[data-look-waistcoat-palette]');
      renderPalette(studio, studio.querySelector('[data-look-waistcoat-design]'), '[data-look-waistcoat-design-palette]');
      const overlay = studio.querySelector('[data-look-scan-dialog]');
      studio.querySelector('[data-look-scan-close]')?.addEventListener('click', () => closeScan(studio));
      overlay?.addEventListener('click', (event) => { if (event.target === overlay) closeScan(studio); });
      overlay?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeScan(studio);
        if (event.key === 'Tab') {
          event.preventDefault();
          studio.querySelector('[data-look-scan-close]')?.focus?.();
        }
      });
      [
        { linkSelector: '[data-look-scan-preview-link]', optionSelector: null },
        { linkSelector: '[data-look-selected-kameez-link]', optionSelector: '[data-look-kameez]' },
        { linkSelector: '[data-look-selected-waistcoat-link]', optionSelector: 'waistcoat-active' },
        { linkSelector: '[data-look-review-kameez-btn]', optionSelector: '[data-look-kameez]' },
        { linkSelector: '[data-look-review-waistcoat-btn]', optionSelector: 'waistcoat-active' }
      ].forEach(({ linkSelector, optionSelector }) => {
        const link = studio.querySelector(linkSelector);
        if (!link) return;
        const selectedOption = () => {
          if (optionSelector === 'waistcoat-active') return activeWcSelect(studio)?.selectedOptions[0];
          return optionSelector
            ? studio.querySelector(optionSelector)?.selectedOptions[0]
            : studio.querySelector(studio.querySelector('[data-look-pieces]:checked')?.value === 'waistcoat' ? '[data-look-waistcoat]' : '[data-look-kameez]')?.selectedOptions[0];
        };
        link.addEventListener('click', (event) => {
          const option = selectedOption();
          openScan(studio, option?.dataset.scan, option?.value, event.currentTarget, event);
        });
        link.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'touch') return;
          const option = selectedOption();
          const hoverSrc = option?.dataset.quality === 'Worsted Medora'
            ? (option.dataset.frame || option.dataset.scan)
            : option?.dataset.scan;
          showScanHover(studio, hoverSrc, option?.value);
        });
        link.addEventListener('pointerleave', () => hideScanHover(studio));
      });
      studio.querySelectorAll('select, input').forEach((input) => {
        if (input.matches('[data-look-frame-scale]')) return;
        input.addEventListener('change', () => update(studio));
        if (input.type === 'number') input.addEventListener('input', () => update(studio));
      });
      studio.addEventListener?.('click', (event) => {
        const step = event.target.closest?.('[data-look-step]');
        if (!step || step.disabled) return;
        const input = step.parentElement?.querySelector('input[type="number"]');
        if (!input || input.disabled) return;
        if (step.dataset.lookStep === '1') input.stepUp();
        else input.stepDown();
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
      studio.querySelector('[data-look-save]')?.addEventListener('click', () => {
        const saveBtn = studio.querySelector('[data-look-save]');
        if (saveBtn?.disabled) return;
        update(studio);
        const draft = studio.saraDraft;
        if (!draft) return;
        const looks = studio.saraSavedLooks;
        const editing = studio.saraEditingIndex;
        const duplicate = looks.findIndex((look, index) => index !== editing && lookKey(look) === lookKey(draft));
        const error = studio.querySelector('[data-look-error]');
        if (duplicate >= 0) {
          error.textContent = t(studio, 'save_duplicate', `This combination is already saved as look ${duplicate + 1}. Edit its colours or counts before saving again.`, { n: duplicate + 1 });
          error.hidden = false;
          return;
        }
        if (editing === undefined && looks.length >= 20) {
          error.textContent = t(studio, 'save_limit', 'You can save up to 20 looks in one visit. Remove a look to add another.');
          error.hidden = false;
          return;
        }

        const commitSave = (photo) => {
          if (photo) {
            draft.photo = photo;
            draft.photoIsScan = false;
          } else if (!draft.photo) {
            const fallback = scanFallbackPhoto(studio, draft);
            if (fallback) {
              draft.photo = fallback;
              draft.photoIsScan = true;
            }
          }
          if (editing === undefined) looks.push(draft);
          else looks[editing] = draft;
          studio.saraEditingIndex = undefined;
          setActionLabel(saveBtn, t(studio, 'save_another', 'Save another colour combination'));
          studio.querySelector('[data-look-save-feedback]').textContent = t(studio, 'save_feedback', `Look ${editing === undefined ? looks.length : editing + 1} saved. Change colours to make another, or review your saved looks below.`, { n: editing === undefined ? looks.length : editing + 1 });
          error.hidden = true;
          if (saveBtn) saveBtn.disabled = false;
          renderTray(studio);
        };

        /* Wait for try-on compose so Look 1 is not blank while Look 2 gets the thumb. */
        if (studio.saraLayerLook) {
          const ready = captureLayerStackPhoto(studio) || studio.saraLayerPhoto;
          if (ready && studio.saraLayerPhotoKey === layerPhotoKey(studio.saraLayerLook)) {
            commitSave(ready);
            return;
          }
          if (saveBtn) saveBtn.disabled = true;
          const key = layerPhotoKey(studio.saraLayerLook);
          composeLayerPhoto(studio, studio.saraLayerLook).then((dataUrl) => {
            if (dataUrl) {
              studio.saraLayerPhoto = dataUrl;
              studio.saraLayerPhotoKey = key;
            }
            commitSave(dataUrl || captureLayerStackPhoto(studio) || null);
          }).catch(() => commitSave(null));
          return;
        }
        commitSave(draft.photo || null);
      });
      studio.querySelector('[data-look-add-more]')?.addEventListener('click', () => {
        studio.saraEditingIndex = undefined;
        setActionLabel(studio.querySelector('[data-look-save]'), t(studio, 'save_another', 'Save another colour combination'));
        studio.querySelector('[data-look-save-feedback]').textContent = t(studio, 'save_next_hint', 'Change the fabrics above, then save your next look.');
        studio.querySelector('[data-look-kameez]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      studio.querySelector('[data-look-tray-list]')?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-look-action]');
        if (!button) return;
        const index = Number(button.dataset.lookIndex);
        const looks = studio.saraSavedLooks;
        if (!Number.isInteger(index) || index < 0 || index >= looks.length) return;
        if (button.dataset.lookAction === 'remove') {
          looks.splice(index, 1);
          if (studio.saraEditingIndex === index) {
            studio.saraEditingIndex = undefined;
            setActionLabel(studio.querySelector('[data-look-save]'), t(studio, 'save_another', 'Save another colour combination'));
            studio.querySelector('[data-look-save-feedback]').textContent = t(studio, 'edit_removed', 'The look being edited was removed. Change the fabrics above to save a new look.');
          } else if (studio.saraEditingIndex > index) {
            studio.saraEditingIndex -= 1;
            setActionLabel(studio.querySelector('[data-look-save]'), t(studio, 'update_look', `Update look ${studio.saraEditingIndex + 1}`, { n: studio.saraEditingIndex + 1 }));
            studio.querySelector('[data-look-save-feedback]').textContent = t(studio, 'editing_look', `Editing look ${studio.saraEditingIndex + 1}. Save your changes to update it.`, { n: studio.saraEditingIndex + 1 });
          }
          renderTray(studio);
          return;
        }
        if (button.dataset.lookAction !== 'edit') return;
        const look = looks[index];
        studio.saraEditingIndex = index;
        setWcSource(studio, look.wcSource === 'collection' ? 'collection' : 'matching');
        const kSelect = studio.querySelector('[data-look-kameez]');
        const wSelect = look.wcSource === 'collection'
          ? studio.querySelector('[data-look-waistcoat-design]')
          : studio.querySelector('[data-look-waistcoat]');
        const kOk = setSelectToSavedValue(kSelect, look.kValue);
        let wOk = setSelectToSavedValue(wSelect, look.wValue);
        // Matching waistcoat: if the stored WC label drifted, fall back to the
        // same shade as the restored kameez so both piece scans stay visible.
        if (!wOk && look.wcSource !== 'collection' && kSelect?.selectedOptions[0] && wSelect) {
          const kOpt = kSelect.selectedOptions[0];
          wOk = setSelectToSavedValue(wSelect, kOpt.value)
            || setSelectToSavedValue(wSelect, kOpt.dataset.shadeId)
            || setSelectToSavedValue(wSelect, kOpt.dataset.shade)
            || ensureSelectHasOption(wSelect);
        }
        if (!kOk) ensureSelectHasOption(kSelect);
        if (!wOk) ensureSelectHasOption(wSelect);
        studio.querySelector('[data-look-kameez-count]').value = String(look.kCount);
        studio.querySelector('[data-look-waistcoat-count]').value = String(look.wCount);
        studio.querySelector('[data-look-kameez-length]').value = String(look.kMetres);
        studio.querySelector('[data-look-waistcoat-length]').value = String(look.wMetres);
        const piecesValue = look.pieces || 'both';
        studio.querySelectorAll('[data-look-pieces]').forEach((radio) => { radio.checked = radio.value === piecesValue; });
        studio.querySelectorAll('[data-look-cut]').forEach((radio) => { radio.checked = radio.value === (look.custom ? 'custom' : 'standard'); });
        setActionLabel(studio.querySelector('[data-look-save]'), t(studio, 'update_look', `Update look ${index + 1}`, { n: index + 1 }));
        studio.querySelector('[data-look-save-feedback]').textContent = t(studio, 'editing_look', `Editing look ${index + 1}. Save your changes to update it.`, { n: index + 1 });
        update(studio);
        // Prefer scrolling to the try-on + both fabric proofs, not only the kameez field.
        (studio.querySelector('[data-look-selected-scans]')
          || studio.querySelector('[data-look-photo]')
          || studio.querySelector('[data-look-kameez]'))
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      studio.querySelector('[data-look-cart]')?.addEventListener('click', async (event) => {
        const button = event.currentTarget;
        const error = studio.querySelector('[data-look-tray-error]') || studio.querySelector('[data-look-error]');
        update(studio);
        if (!studio.saraCartItems || button.disabled || studio.saraSubmitting) {
          if (!studio.saraSubmitting) {
            error.textContent = studio.querySelector('[data-look-tray-note]')?.textContent
              || t(studio, 'cart_unavailable', 'These cuts are not available for cart checkout.');
            error.hidden = false;
          }
          return;
        }
        const items = studio.saraCartItems;
        const addedLooks = (studio.saraSavedLooks || []).filter((look) => look.cartItems?.length).length;
        studio.saraSubmitting = true;
        button.disabled = true;
        error.hidden = true;
        try {
          const routes = cartRoots();
          const sections = ['cart-icon-bubble', 'cart-notification-button'];
          const response = await fetch(routes.add, {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items,
              sections,
              sections_url: window.location.pathname
            })
          });
          const parsed = await response.json().catch(() => null);
          if (!response.ok || (response.redirected && new URL(response.url).pathname === '/password')) {
            throw new Error(parsed?.description || parsed?.message || t(studio, 'cart_failed', 'The cart could not accept those cuts. Please check availability and try again.'));
          }
          if (parsed?.sections) {
            replaceSectionHtml('cart-icon-bubble', parsed.sections['cart-icon-bubble']);
            replaceSectionHtml('cart-notification-button', parsed.sections['cart-notification-button']);
          }
          const lookWord = addedLooks === 1
            ? t(studio, 'look_word_one', 'look')
            : t(studio, 'look_word_other', 'looks');
          showCartToast(studio, { count: addedLooks || items.length, lookWord });
          studio.saraSavedLooks = (studio.saraSavedLooks || []).filter((look) => !look.cartItems?.length);
          studio.saraEditingIndex = undefined;
          setActionLabel(studio.querySelector('[data-look-save]'), t(studio, 'save_another', 'Save another colour combination'));
          const feedback = studio.querySelector('[data-look-save-feedback]');
          if (feedback) {
            feedback.textContent = t(studio, 'cart_toast_continue_hint', 'Eligible standard looks are in your cart. Change colours above to save another combination.');
          }
          renderTray(studio);
        } catch (failure) {
          error.textContent = failure.message;
          error.hidden = false;
        } finally {
          studio.saraSubmitting = false;
          button.disabled = false;
          update(studio);
        }
      });
      restoreLooks(studio);
      applyShadeFromUrl(studio);
      update(studio);
      if (studio.saraSavedLooks?.length) {
        const feedback = studio.querySelector('[data-look-save-feedback]');
        if (feedback && !feedback.textContent) {
          feedback.textContent = t(studio, 'looks_restored', `${studio.saraSavedLooks.length} saved look(s) restored from this device.`, { count: studio.saraSavedLooks.length });
        }
      }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/* Stacked layouts (phones, narrow tablets): the pinned try-on slid over the step-03 quantity and cut controls (owner 10-30).
   Unpin it once the step-03 heading reaches the pin's bottom edge; scrolling back up pins it again. Side-by-side layouts are left alone. */
(function () {
  function bind(studio) {
    if (studio.dataset.sfPinWatch) return;
    var step = studio.querySelector('[id^="sf-look-qty-cut-title-"]');
    if (!step) return;
    studio.dataset.sfPinWatch = '1';
    var queued = false;
    function update() {
      queued = false;
      studio.classList.remove('sf-look-studio--unpin');   // measure where the pin would sit
      var pin = null;
      studio.querySelectorAll('.sf-look-studio__model-panel, .sf-look-studio__figure').forEach(function (el) {
        if (!pin && getComputedStyle(el).position === 'sticky' && el.getBoundingClientRect().height) pin = el;   // phones: the panel is display: contents
      });
      if (!pin) return;
      var p = pin.getBoundingClientRect(), s = step.getBoundingClientRect();
      var stacked = p.left < s.right && s.left < p.right;
      if (stacked && s.top < p.bottom) studio.classList.add('sf-look-studio--unpin');
    }
    function queue() { if (!queued) { queued = true; requestAnimationFrame(update); } }
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    update();
  }
  function init() { document.querySelectorAll('.sf-look-studio--visual-preview').forEach(bind); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
